// src/components/VideoCallPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import axios from 'axios';
import { Video, X, Mic, MicOff, VideoOff, Settings, Share2, Users, Loader2, Monitor, MonitorOff, Circle, Square, Maximize2, Check, Phone, MessageSquare } from 'lucide-react';
import { useMeetingChat } from '../hooks/useMeetingChat';
import ChatPanel from './ChatPanel';
import { API_BASE_URL } from '../config/api';

// Use centralized API config
const API_BASE = API_BASE_URL;

const VideoCallPage = () => {
  const [channelName, setChannelName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inMeeting, setInMeeting] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [remoteUserNames, setRemoteUserNames] = useState({}); // Stocker les noms des utilisateurs distants
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [client, setClient] = useState(null);
  const [agoraConfig, setAgoraConfig] = useState(null);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenTrack, setScreenTrack] = useState(null);
  const [remoteScreenShares, setRemoteScreenShares] = useState({}); // uid -> screenTrack
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [canvasStream, setCanvasStream] = useState(null);
  const canvasRef = useRef(null);
  const videoContainerRef = useRef(null);
  const captureIntervalRef = useRef(null); // Référence pour l'intervalle de capture
  const combinedStreamRef = useRef(null); // Référence pour le stream combiné (pour retirer les tracks audio avant arrêt)
  const [selectedStreamForMainView, setSelectedStreamForMainView] = useState(null); // { type: 'screen' | 'video', uid: number }
  const [dataStream, setDataStream] = useState(null); // Référence au data stream Agora pour fallback
  const [chatMinimized, setChatMinimized] = useState(false); // État de minimisation du chat

  const localVideoRef = useRef(null);
  const remoteVideosRef = useRef({});
  const screenShareRef = useRef(null);

  // Hook personnalisé pour le chat
  const chat = useMeetingChat({
    channelName: agoraConfig?.channel || channelName,
    displayName: displayName,
    clientUid: client?.uid,
    client: client,
    dataStream: dataStream,
    onError: (errorMsg) => {
      setError(errorMsg);
      setTimeout(() => setError(''), 5000);
    }
  });

  // Récupérer le nom d'utilisateur depuis le localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.name || user.email) {
      setDisplayName(user.name || user.email.split('@')[0]);
    }
  }, []);

  // Générer un nom de channel unique
  const generateChannelName = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    return `supplychain-${timestamp}-${random}`;
  };

  // Démarrer la réunion
  const startMeeting = async () => {
    if (!displayName.trim()) {
      setError('Veuillez entrer votre nom');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Vous devez être connecté pour créer une réunion');
      }

      const channel = channelName || generateChannelName();

      // Appeler l'API backend pour obtenir le token Agora
      const response = await axios.post(
        `${API_BASE}/agora/token`,
        { channelName: channel },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setAgoraConfig(response.data);
        setChannelName(response.data.channel);
        await joinChannel(response.data);
      } else {
        throw new Error(response.data.message || 'Erreur lors de la création de la réunion');
      }
    } catch (err) {
      console.error('Erreur création réunion:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Impossible de créer la réunion';
      setError(errorMessage);
      
      if (err.response?.data?.error === 'MISSING_APP_ID' || err.response?.data?.error === 'MISSING_APP_CERTIFICATE') {
        setError('Configuration Agora incomplète. Veuillez configurer AGORA_APP_ID et AGORA_APP_CERTIFICATE dans les variables d\'environnement.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Rejoindre le channel Agora
  const joinChannel = async (config) => {
    try {
      // Créer le client Agora
      const agoraClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      setClient(agoraClient);

      // Écouter les messages de données pour recevoir les noms d'utilisateur et messages de chat
      agoraClient.on('stream-message', (uid, data) => {
        try {
          const message = JSON.parse(new TextDecoder().decode(data));
          
          if (message.type === 'userName' && message.name) {
            console.log('Nom reçu pour UID', uid, ':', message.name);
            setRemoteUserNames((prevNames) => ({
              ...prevNames,
              [uid]: message.name
            }));
            // Mettre à jour aussi l'affichage immédiatement
            setRemoteUsers((prev) => {
              return prev.map(u => {
                if (u.uid === uid) {
                  return { ...u };
                }
                return u;
              });
            });
          } else if (message.type === 'chat-message') {
            // Message de chat via Agora (fallback si WebSocket n'est pas disponible)
            const chatMessage = {
              id: `agora-${Date.now()}-${uid}`,
              channelName: config.channel,
              senderUid: uid,
              senderName: message.senderName || remoteUserNames[uid] || `Utilisateur ${uid}`,
              message: message.message,
              timestamp: message.timestamp || Date.now(),
              messageType: 'text',
              source: 'agora' // Pour distinguer de WebSocket
            };
            
            // Utiliser la fonction addMessage du hook
            if (chat.addMessage) {
              chat.addMessage(chatMessage);
            }
          }
        } catch (e) {
          console.warn('Erreur parsing message:', e);
          // Essayer de parser comme string si JSON échoue
          try {
            const text = new TextDecoder().decode(data);
            if (text.includes('userName') || text.includes('name')) {
              console.log('Message texte reçu:', text);
            }
          } catch (e2) {
            console.warn('Impossible de parser le message:', e2);
          }
        }
      });

      // Écouter les événements
      agoraClient.on('user-published', async (user, mediaType) => {
        await agoraClient.subscribe(user, mediaType);
        console.log('User published:', user, mediaType, 'uid:', user.uid);

        // Pour la vidéo, attendre un peu que le track soit disponible
        if (mediaType === 'video') {
          // Attendre que le videoTrack soit disponible (peut prendre un moment après subscribe)
          let retries = 0;
          while (!user.videoTrack && retries < 10) {
            await new Promise(resolve => setTimeout(resolve, 100));
            retries++;
          }
          console.log('VideoTrack disponible après', retries, 'tentatives:', user.videoTrack ? 'OUI' : 'NON');
          
          // Vérifier si c'est un écran partagé (ScreenVideoTrack)
          // IMPORTANT: Agora publie les écrans partagés comme des tracks vidéo normaux
          // On doit les détecter différemment
          if (user.videoTrack) {
            let isScreenShare = false;
            
            try {
              // Méthode 1: Vérifier le label du track (le plus fiable)
              if (user.videoTrack.getMediaStreamTrack) {
                const track = user.videoTrack.getMediaStreamTrack();
                if (track) {
                  const label = track.label || '';
                  const labelLower = label.toLowerCase();
                  
                  // Vérifier les mots-clés dans le label
                  if (labelLower.includes('screen') || 
                      labelLower.includes('display') || 
                      labelLower.includes('monitor') ||
                      labelLower.includes('window') ||
                      labelLower.includes('desktop')) {
                    isScreenShare = true;
                    console.log('✅ Écran partagé détecté via label pour UID:', user.uid, 'label:', label);
                  }
                  
                  // Méthode 2: Vérifier via getSettings (displaySurface)
                  if (!isScreenShare && track.getSettings) {
                    try {
                      const settings = track.getSettings();
                      if (settings && (settings.displaySurface || settings.logicalSurface)) {
                        isScreenShare = true;
                        console.log('✅ Écran partagé détecté via getSettings pour UID:', user.uid);
                      }
                    } catch (e) {
                      // Ignorer les erreurs
                    }
                  }
                  
                  // Méthode 3: Vérifier via getConstraints
                  if (!isScreenShare && track.getConstraints) {
                    try {
                      const constraints = track.getConstraints();
                      if (constraints && (constraints.displaySurface || constraints.logicalSurface)) {
                        isScreenShare = true;
                        console.log('✅ Écran partagé détecté via getConstraints pour UID:', user.uid);
                      }
                    } catch (e) {
                      // Ignorer les erreurs
                    }
                  }
                }
              }
              
              // Méthode 4: Vérifier via les propriétés du track Agora
              if (!isScreenShare) {
                // Les ScreenVideoTrack d'Agora ont souvent certaines propriétés
                const trackType = user.videoTrack.trackMediaType || user.videoTrack.getTrackLabel?.() || '';
                if (typeof trackType === 'string' && trackType.toLowerCase().includes('screen')) {
                  isScreenShare = true;
                  console.log('✅ Écran partagé détecté via trackMediaType pour UID:', user.uid);
                }
              }
              
              // Si détecté comme écran partagé, l'ajouter à remoteScreenShares
              if (isScreenShare) {
                setRemoteScreenShares(prev => {
                  const newShares = { ...prev };
                  newShares[user.uid] = user.videoTrack;
                  console.log('✅ Écran partagé ajouté à remoteScreenShares pour UID:', user.uid);
                  return newShares;
                });
              } else {
                console.log('📹 Track vidéo normal (caméra) pour UID:', user.uid);
              }
            } catch (e) {
              console.warn('Erreur détection écran partagé pour UID', user.uid, ':', e);
            }
          }
        }

        // Vérifier si c'est un écran partagé AVANT d'ajouter à remoteUsers
        // Si c'est un écran partagé, on ne l'ajoute pas comme vidéo normale
        const isScreenShareTrack = mediaType === 'video' && user.videoTrack && (() => {
          try {
            if (user.videoTrack.getMediaStreamTrack) {
              const track = user.videoTrack.getMediaStreamTrack();
              if (track) {
                const label = track.label || '';
                const labelLower = label.toLowerCase();
                if (labelLower.includes('screen') || labelLower.includes('display') || labelLower.includes('monitor')) {
                  return true;
                }
                if (track.getSettings) {
                  const settings = track.getSettings();
                  if (settings && (settings.displaySurface || settings.logicalSurface)) {
                    return true;
                  }
                }
              }
            }
          } catch (e) {
            // Ignorer
          }
          return false;
        })();

        // Ajouter ou mettre à jour l'utilisateur dans la liste (audio ou vidéo)
        // IMPORTANT: Ne pas ajouter les écrans partagés comme vidéos normales
        setRemoteUsers((prev) => {
          const existingUser = prev.find(u => u.uid === user.uid);
          if (existingUser) {
            // Mettre à jour l'utilisateur existant avec les nouvelles informations
            return prev.map(u => {
              if (u.uid === user.uid) {
                const updatedUser = { 
                  ...u, 
                  uid: user.uid,
                  // Si c'est un écran partagé, ne pas marquer comme hasVideo (pour éviter l'affichage dans la section vidéos)
                  hasVideo: mediaType === 'video' && !isScreenShareTrack ? true : (u.hasVideo || false),
                  hasAudio: mediaType === 'audio' ? true : (u.hasAudio || false),
                  // Ne pas stocker le track vidéo si c'est un écran partagé (il est dans remoteScreenShares)
                  videoTrack: mediaType === 'video' && !isScreenShareTrack ? (user.videoTrack || u.videoTrack) : (isScreenShareTrack ? null : u.videoTrack),
                  audioTrack: mediaType === 'audio' ? (user.audioTrack || u.audioTrack) : u.audioTrack
                };
                console.log('Updated user:', updatedUser.uid, 'hasVideo:', updatedUser.hasVideo, 'isScreenShare:', isScreenShareTrack);
                return updatedUser;
              }
              return u;
            });
          } else {
            // Ajouter un nouvel utilisateur
            const newUser = { 
              uid: user.uid,
              // Si c'est un écran partagé, ne pas marquer comme hasVideo
              hasVideo: mediaType === 'video' && !isScreenShareTrack,
              hasAudio: mediaType === 'audio',
              // Ne pas stocker le track vidéo si c'est un écran partagé
              videoTrack: mediaType === 'video' && !isScreenShareTrack ? user.videoTrack : null,
              audioTrack: mediaType === 'audio' ? user.audioTrack : null
            };
            console.log('New user added:', newUser.uid, 'hasVideo:', newUser.hasVideo, 'isScreenShare:', isScreenShareTrack);
            
            // Récupérer immédiatement le nom depuis le backend
            if (agoraConfig) {
              const fetchUserName = async () => {
                try {
                  const token = localStorage.getItem('token');
                  if (token) {
                    const response = await axios.post(
                      `${API_BASE}/agora/participants`,
                      { channelName: agoraConfig.channel },
                      {
                        headers: {
                          Authorization: `Bearer ${token}`
                        }
                      }
                    );
                    if (response.data.success && response.data.participants && response.data.participants[user.uid]) {
                      setRemoteUserNames((prevNames) => ({
                        ...prevNames,
                        [user.uid]: response.data.participants[user.uid]
                      }));
                      console.log('Nom récupéré pour UID', user.uid, ':', response.data.participants[user.uid]);
                    }
                  }
                } catch (fetchError) {
                  console.warn('Erreur récupération nom utilisateur:', fetchError);
                }
              };
              fetchUserName();
            }
            
            return [...prev, newUser];
          }
        });

        if (mediaType === 'audio') {
          user.audioTrack?.play();
        }
      });

      agoraClient.on('user-unpublished', (user, mediaType) => {
        console.log('User unpublished:', user, mediaType);
        // Mettre à jour l'utilisateur au lieu de le supprimer
        setRemoteUsers((prev) => {
          return prev.map(u => {
            if (u.uid === user.uid) {
              // Mettre à jour le statut média de l'utilisateur
              if (mediaType === 'video') {
                // Vérifier si c'était un écran partagé
                if (remoteScreenShares[user.uid]) {
                  setRemoteScreenShares(prev => {
                    const newShares = { ...prev };
                    delete newShares[user.uid];
                    return newShares;
                  });
                }
                return { ...u, hasVideo: false, videoTrack: null };
              } else if (mediaType === 'audio') {
                return { ...u, hasAudio: false, audioTrack: null };
              }
            }
            return u;
          });
        });
      });

      agoraClient.on('user-left', async (user) => {
        console.log('User left:', user);
        setRemoteUsers((prev) => prev.filter(u => u.uid !== user.uid));
        setRemoteUserNames((prevNames) => {
          const newNames = { ...prevNames };
          delete newNames[user.uid];
          return newNames;
        });
        
        // Retirer le participant du backend
        try {
          const token = localStorage.getItem('token');
          if (token && agoraConfig) {
            await axios.post(
              `${API_BASE}/agora/remove-participant`,
              {
                channelName: agoraConfig.channel,
                uid: user.uid
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );
          }
        } catch (removeError) {
          console.warn('Erreur retrait participant:', removeError);
        }
      });

      // Rejoindre le channel - C'est ici que la validation réelle des credentials se fait
      let actualUid = null;
      try {
        await agoraClient.join(config.appId, config.channel, config.token, config.uid || null);
        // Récupérer le vrai UID après la connexion
        actualUid = agoraClient.uid;
        console.log('Connexion à Agora réussie - Credentials valides, UID:', actualUid);
        
        // Enregistrer le participant dans le backend avec son vrai UID
        try {
          const token = localStorage.getItem('token');
          if (token) {
            await axios.post(
              `${API_BASE}/agora/register-participant`,
              {
                channelName: config.channel,
                uid: actualUid,
                displayName: displayName || 'Utilisateur'
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );
            console.log('Participant enregistré dans le backend:', displayName, 'UID:', actualUid);
          }
        } catch (registerError) {
          console.warn('Erreur enregistrement participant:', registerError);
        }
      } catch (joinError) {
        console.error('Erreur lors de la connexion à Agora (join):', joinError);
        // Si l'erreur est liée au token ou aux credentials, c'est ici qu'on le détecte
        if (joinError.message && (
          joinError.message.includes('INVALID_TOKEN') ||
          joinError.message.includes('INVALID_APP_ID') ||
          joinError.message.includes('DYNAMIC_KEY_EXPIRED') ||
          joinError.message.includes('token') && joinError.message.includes('invalid')
        )) {
          throw new Error('INVALID_AGORA_CREDENTIALS: Les credentials Agora sont invalides. Vérifiez APP_ID et APP_CERTIFICATE dans le .env du serveur.');
        }
        throw joinError;
      }
      
      // Créer un data stream pour envoyer le nom d'utilisateur (après avoir rejoint)
      let dataStream = null;
      const userName = displayName || 'Utilisateur';
      try {
        dataStream = await agoraClient.createDataStream({ ordered: true });
        const nameData = JSON.stringify({ type: 'userName', name: userName });
        const encoder = new TextEncoder();
        
        // Fonction pour envoyer le nom
        const sendUserName = () => {
          try {
            agoraClient.sendStreamMessage(dataStream, encoder.encode(nameData));
            console.log('Nom d\'utilisateur envoyé:', userName);
          } catch (e) {
            console.warn('Erreur envoi nom:', e);
          }
        };
        
        // Envoyer immédiatement
        sendUserName();
        
        // Renvoyer plusieurs fois pour s'assurer que tous les participants le reçoivent
        setTimeout(sendUserName, 500);
        setTimeout(sendUserName, 1500);
        setTimeout(sendUserName, 3000);
        
        // Envoyer périodiquement (toutes les 10 secondes) pour les nouveaux participants
        const nameInterval = setInterval(() => {
          if (dataStream && agoraClient) {
            sendUserName();
          } else {
            clearInterval(nameInterval);
          }
        }, 10000);
        
        // Nettoyer l'intervalle quand on quitte
        agoraClient.on('connection-state-change', (curState, revState) => {
          if (curState === 'DISCONNECTED' || curState === 'FAILED') {
            clearInterval(nameInterval);
          }
        });
      } catch (streamError) {
        console.warn('Impossible de créer/envoyer via le data stream:', streamError);
      }

      // Créer et publier les tracks locaux avec gestion d'erreurs
      let audioTrack = null;
      let videoTrack = null;
      const tracksToPublish = [];

      try {
        // Essayer de créer le track audio
        try {
          audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
          setLocalAudioTrack(audioTrack);
          tracksToPublish.push(audioTrack);
          setIsAudioEnabled(true);
        } catch (audioError) {
          console.warn('Impossible d\'accéder au microphone:', audioError);
          setError('Microphone non disponible. Vous pouvez continuer sans audio.');
        }

        // Essayer de créer le track vidéo
        try {
          // Récupérer la liste des caméras disponibles
          const cameras = await AgoraRTC.getCameras();
          setAvailableCameras(cameras);
          
          let videoTrack = null;
          let cameraUsed = false;
          
          // Si plusieurs caméras sont disponibles, essayer chacune
          if (cameras.length > 1) {
            for (const camera of cameras) {
              try {
                videoTrack = await AgoraRTC.createCameraVideoTrack({ cameraId: camera.deviceId });
                setSelectedCameraId(camera.deviceId);
                cameraUsed = true;
                break;
              } catch (err) {
                console.log(`Caméra ${camera.label} non disponible, essai suivant...`);
                continue;
              }
            }
          }
          
          // Si aucune caméra spécifique n'a fonctionné, essayer la caméra par défaut
          if (!cameraUsed) {
            videoTrack = await AgoraRTC.createCameraVideoTrack();
          }
          
          setLocalVideoTrack(videoTrack);
          tracksToPublish.push(videoTrack);
          setIsVideoEnabled(true);
        } catch (videoError) {
          console.warn('Impossible d\'accéder à la caméra:', videoError);
          // Continuer sans vidéo si la caméra n'est pas disponible
          setIsVideoEnabled(false);
          if (!audioTrack) {
            // Si ni audio ni vidéo, afficher une erreur
            throw new Error('Microphone et caméra non disponibles. Veuillez vérifier les permissions de votre navigateur.');
          }
        }

        // Publier les tracks disponibles
        if (tracksToPublish.length > 0) {
          await agoraClient.publish(tracksToPublish);
          console.log('Tracks publiés avec succès:', tracksToPublish.length);
        } else {
          throw new Error('Aucun périphérique média disponible');
        }

        // Afficher la vidéo locale après publication (pour s'assurer que tout est prêt)
        if (videoTrack && localVideoRef.current) {
          try {
            // S'assurer que l'élément est visible (spécifique Chrome)
            localVideoRef.current.style.display = 'block';
            // Forcer un reflow pour Chrome
            await new Promise(resolve => setTimeout(resolve, 50));
            // Utiliser safePlay pour une meilleure compatibilité Chrome
            await safePlay(videoTrack, localVideoRef.current, 'local-video');
            console.log('Vidéo locale affichée');
            
            // Vérification spécifique Chrome après un délai
            setTimeout(() => {
              if (localVideoRef.current) {
                const videoEl = localVideoRef.current.querySelector('video');
                if (!videoEl || videoEl.paused) {
                  console.warn('⚠️ Chrome: Réessai affichage vidéo locale dans joinChannel');
                  safePlay(videoTrack, localVideoRef.current, 'local-video').catch(console.error);
                }
              }
            }, 300);
          } catch (playError) {
            console.warn('Erreur affichage vidéo locale:', playError);
            // Réessayer une fois pour Chrome
            setTimeout(async () => {
              if (localVideoRef.current && videoTrack) {
                try {
                  await safePlay(videoTrack, localVideoRef.current, 'local-video');
                } catch (retryError) {
                  console.error('Erreur réessai vidéo locale:', retryError);
                }
              }
            }, 500);
          }
        }

        // Le nom est déjà envoyé via le data stream créé plus tôt
        // Pas besoin de le renvoyer ici car il est envoyé périodiquement

        setInMeeting(true);
        
        // Récupérer les noms des participants depuis le backend
        const fetchParticipants = async () => {
          try {
            const token = localStorage.getItem('token');
            if (token) {
              const response = await axios.post(
                `${API_BASE}/agora/participants`,
                { channelName: config.channel },
                {
                  headers: {
                    Authorization: `Bearer ${token}`
                  }
                }
              );
              if (response.data.success && response.data.participants) {
                // Convertir les UIDs en nombres pour correspondre aux UIDs d'Agora
                const participants = {};
                Object.keys(response.data.participants).forEach(uidStr => {
                  const uid = parseInt(uidStr, 10);
                  if (!isNaN(uid)) {
                    participants[uid] = response.data.participants[uidStr];
                  }
                });
                setRemoteUserNames(prev => ({ ...prev, ...participants }));
                console.log('Noms des participants récupérés:', participants);
              }
            }
          } catch (fetchError) {
            console.warn('Erreur récupération participants:', fetchError);
          }
        };
        
        // Récupérer immédiatement
        fetchParticipants();
        
        // Récupérer périodiquement (toutes les 3 secondes)
        const participantsInterval = setInterval(fetchParticipants, 3000);
        
        // Nettoyer l'intervalle quand on quitte
        agoraClient.on('connection-state-change', (curState, revState) => {
          if (curState === 'DISCONNECTED' || curState === 'FAILED') {
            clearInterval(participantsInterval);
          }
        });
        
        // Stocker l'intervalle pour le nettoyer plus tard
        if (agoraClient) {
          agoraClient._participantsInterval = participantsInterval;
        }
      } catch (err) {
        console.error('Erreur lors de la création des tracks:', err);
        // Quitter le channel si on ne peut pas publier
        await agoraClient.leave();
        throw err;
      }
    } catch (err) {
      console.error('Erreur lors de la connexion à Agora:', err);
      
      // Messages d'erreur plus explicites
      let errorMessage = 'Impossible de se connecter à la réunion. ';
      
      // Détecter les erreurs liées aux credentials Agora invalides
      if (err.message.includes('INVALID_TOKEN') || 
          err.message.includes('INVALID_APP_ID') || 
          err.message.includes('token') && err.message.includes('invalid') ||
          err.message.includes('DYNAMIC_KEY_EXPIRED') ||
          err.message.includes('INVALID_APP_CERTIFICATE')) {
        errorMessage = 'Erreur de configuration Agora : Les credentials (APP_ID ou APP_CERTIFICATE) sont invalides ou incorrects. ';
        errorMessage += 'Veuillez vérifier que vos credentials Agora sont corrects dans le fichier .env du serveur.';
        console.error('CREDENTIALS INVALIDES DÉTECTÉS - Erreur Agora:', err.message);
      } else if (err.message.includes('NotReadableError') || err.message.includes('NOT_READABLE')) {
        errorMessage += 'La caméra est déjà utilisée par un autre navigateur ou application. ';
        errorMessage += 'Limitation système : une seule application peut utiliser une caméra à la fois. ';
        errorMessage += 'Solutions : 1) Fermez l\'autre navigateur/application, 2) Utilisez une autre caméra si vous en avez plusieurs, 3) Continuez en mode audio seulement.';
      } else if (err.message.includes('NotAllowedError') || err.message.includes('PERMISSION_DENIED')) {
        errorMessage += 'Permission refusée. Veuillez autoriser l\'accès à la caméra et au microphone dans les paramètres de votre navigateur.';
      } else if (err.message.includes('NotFoundError') || err.message.includes('DEVICE_NOT_FOUND')) {
        errorMessage += 'Aucune caméra ou microphone détecté. Veuillez connecter un périphérique et réessayer.';
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
      
      // Nettoyer les ressources en cas d'erreur
      if (client) {
        try {
          await client.leave();
        } catch (leaveErr) {
          console.error('Erreur lors de la déconnexion:', leaveErr);
        }
      }
    }
  };

  // Quitter la réunion
  const leaveMeeting = async () => {
    try {
      // Retirer le participant du backend avant de quitter
      if (client && agoraConfig) {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const actualUid = client.uid;
            await axios.post(
              `${API_BASE}/agora/remove-participant`,
              {
                channelName: agoraConfig.channel,
                uid: actualUid
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );
          }
        } catch (removeError) {
          console.warn('Erreur retrait participant:', removeError);
        }
        
        // Nettoyer l'intervalle de récupération des participants
        if (client._participantsInterval) {
          clearInterval(client._participantsInterval);
        }
      }
      
      // Arrêter et supprimer les tracks locaux
      if (localAudioTrack) {
        localAudioTrack.stop();
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.stop();
        localVideoTrack.close();
      }
      if (screenTrack) {
        screenTrack.stop();
        screenTrack.close();
        setScreenTrack(null);
        setIsScreenSharing(false);
      }

      // Quitter le channel
      if (client) {
        await client.leave();
      }

      // Nettoyer les refs
      setLocalAudioTrack(null);
      setLocalVideoTrack(null);
      setClient(null);
      setRemoteUsers([]);
      setRemoteUserNames({});
      setAgoraConfig(null);
      setInMeeting(false);
      setChannelName('');
      setCopied(false);
      setDataStream(null);
    } catch (err) {
      console.error('Erreur lors de la déconnexion:', err);
    }
  };

  // Toggle audio
  const toggleAudio = async () => {
    if (localAudioTrack) {
      try {
        await localAudioTrack.setMuted(isAudioEnabled);
        setIsAudioEnabled(!isAudioEnabled);
      } catch (err) {
        console.error('Erreur lors du toggle audio:', err);
      }
    }
  };

  // Toggle video
  const toggleVideo = async () => {
    // Ne pas permettre de modifier la vidéo pendant l'enregistrement ou le partage d'écran
    if (isRecording) {
      setError('⚠️ Impossible de modifier la vidéo pendant l\'enregistrement. Arrêtez d\'abord l\'enregistrement.');
      setTimeout(() => setError(''), 5000);
      return;
    }

    if (isScreenSharing) {
      setError('⚠️ Impossible de modifier la vidéo pendant le partage d\'écran. Arrêtez d\'abord le partage d\'écran.');
      setTimeout(() => setError(''), 5000);
      return;
    }

    if (localVideoTrack && client) {
      try {
        if (isVideoEnabled) {
          // Désactiver complètement la vidéo - arrêter la caméra
          try {
            // Vérifier l'état de connexion avant d'unpublish
            const connectionState = client.connectionState;
            if (connectionState === 'CONNECTED' || connectionState === 'CONNECTING') {
              await client.unpublish([localVideoTrack]);
            }
          } catch (unpublishErr) {
            console.warn('Erreur unpublish vidéo:', unpublishErr);
          }
          
          // Arrêter et nettoyer le track
          try {
            localVideoTrack.stop();
            localVideoTrack.close();
          } catch (stopErr) {
            console.warn('Erreur arrêt track:', stopErr);
          }
          
          // Nettoyer les références
          setLocalVideoTrack(null);
          setIsVideoEnabled(false);
          playingTracksRef.current.delete('local-video');
          
          // S'assurer que l'élément vidéo est masqué
          if (localVideoRef.current) {
            localVideoRef.current.style.display = 'none';
            // Nettoyer l'élément vidéo
            const videoElement = localVideoRef.current.querySelector('video');
            if (videoElement) {
              videoElement.srcObject = null;
            }
          }
        } else {
          // Réactiver la vidéo - recréer le track
          try {
            // Essayer de trouver une caméra disponible
            let cameras = [];
            try {
              cameras = await safeGetCameras();
              setAvailableCameras(cameras);
            } catch (camerasErr) {
              console.warn('Impossible d\'obtenir la liste des caméras:', camerasErr);
            }
            
            let videoTrack = null;
            let cameraUsed = false;
            
            // Essayer d'utiliser une caméra différente si plusieurs sont disponibles
            if (cameras.length > 1) {
              // Essayer chaque caméra jusqu'à en trouver une disponible
              for (const camera of cameras) {
                try {
                  videoTrack = await safeCreateVideoTrack(camera.deviceId);
                  setSelectedCameraId(camera.deviceId);
                  cameraUsed = true;
                  break;
                } catch (err) {
                  console.log(`Caméra ${camera.label} non disponible, essai suivant...`);
                  continue;
                }
              }
            }
            
            // Si aucune caméra spécifique n'a fonctionné, essayer la caméra par défaut
            if (!cameraUsed) {
              videoTrack = await safeCreateVideoTrack();
            }
            
            setLocalVideoTrack(videoTrack);
            
            // Publier le track avec la fonction sécurisée
            const connectionState = client.connectionState;
            if (connectionState === 'CONNECTED' || connectionState === 'CONNECTING') {
              await safePublishVideoTrack(videoTrack);
            }
            
            setIsVideoEnabled(true);
            
            // Afficher la vidéo après publication
            if (localVideoRef.current) {
              // S'assurer que l'élément est visible (spécifique Chrome)
              localVideoRef.current.style.display = 'block';
              // Forcer un reflow pour Chrome
              await new Promise(resolve => setTimeout(resolve, 50));
              // Jouer le track de manière sécurisée
              await safePlay(videoTrack, localVideoRef.current, 'local-video');
              
              // Vérification spécifique Chrome
              setTimeout(() => {
                if (localVideoRef.current) {
                  const videoEl = localVideoRef.current.querySelector('video');
                  if (!videoEl || videoEl.paused) {
                    console.warn('⚠️ Chrome: Réessai affichage vidéo locale dans toggleVideo');
                    safePlay(videoTrack, localVideoRef.current, 'local-video').catch(console.error);
                  }
                }
              }, 300);
            }
            setError('');
          } catch (reactivateErr) {
            console.error('Erreur réactivation caméra:', reactivateErr);
            if (reactivateErr.message.includes('Permission denied') || reactivateErr.message.includes('PERMISSION_DENIED')) {
              setError('Permission refusée. Cliquez sur l\'icône de cadenas et autorisez la caméra.');
            } else if (reactivateErr.message.includes('NotReadableError') || reactivateErr.message.includes('NOT_READABLE')) {
              setError('La caméra est déjà utilisée par un autre navigateur ou application. Fermez les autres onglets/applications vidéo ou utilisez une autre caméra si disponible.');
            } else {
              setError('Impossible de réactiver la caméra: ' + reactivateErr.message);
            }
            setTimeout(() => setError(''), 5000);
          }
        }
      } catch (err) {
        console.error('Erreur lors du toggle vidéo:', err);
        setError('Erreur lors de la modification de la vidéo: ' + (err.message || 'Erreur inconnue'));
        setTimeout(() => setError(''), 5000);
      }
    } else if (!localVideoTrack && client) {
      // Si pas de track mais client disponible, créer un nouveau track
      try {
        const videoTrack = await AgoraRTC.createCameraVideoTrack();
        setLocalVideoTrack(videoTrack);
        
        const connectionState = client.connectionState;
        if (connectionState === 'CONNECTED' || connectionState === 'CONNECTING') {
          await safePublishVideoTrack(videoTrack);
        }
        
        setIsVideoEnabled(true);
        
        if (localVideoRef.current) {
          localVideoRef.current.style.display = 'block';
          // Forcer un reflow pour Chrome
          await new Promise(resolve => setTimeout(resolve, 50));
          await safePlay(videoTrack, localVideoRef.current, 'local-video');
          
          // Vérification spécifique Chrome
          setTimeout(() => {
            if (localVideoRef.current) {
              const videoEl = localVideoRef.current.querySelector('video');
              if (!videoEl || videoEl.paused) {
                console.warn('⚠️ Chrome: Réessai affichage vidéo locale dans toggleVideo (else)');
                safePlay(videoTrack, localVideoRef.current, 'local-video').catch(console.error);
              }
            }
          }, 300);
        }
        setError('');
      } catch (err) {
        console.error('Erreur création track vidéo:', err);
        setError('Impossible d\'activer la caméra: ' + (err.message || 'Erreur inconnue'));
        setTimeout(() => setError(''), 5000);
      }
    }
  };

  // Copier le lien de la réunion
  const copyRoomLink = () => {
    const roomLink = `${window.location.origin}/video-call?room=${channelName}`;
    navigator.clipboard.writeText(roomLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  

  // Démarrer l'enregistrement de la zone de réunion (sans notification de partage)
  const startLocalRecording = async () => {
    if (!client) {
      setError('Vous devez être dans une réunion pour enregistrer');
      return;
    }

    try {
      // Attendre que le conteneur de vidéos soit disponible
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Trouver le conteneur principal de la réunion
      const videoContainer = document.querySelector('.relative.min-h-screen.bg-slate-900');
      if (!videoContainer) {
        throw new Error('Zone de réunion non trouvée');
      }

      // Créer un canvas pour capturer toute la zone de réunion
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Utiliser la taille réelle du conteneur
      const rect = videoContainer.getBoundingClientRect();
      canvas.width = rect.width || window.innerWidth;
      canvas.height = rect.height || window.innerHeight;
      canvasRef.current = canvas;

      // Collecter tous les tracks audio des participants
      // IMPORTANT: On clone les tracks pour l'enregistrement, on ne les arrête pas après
      const audioTracks = [];

      // Ajouter le track audio local (cloner pour ne pas affecter la réunion)
      if (localAudioTrack && isAudioEnabled) {
        try {
          const audioStream = localAudioTrack.getMediaStreamTrack();
          // Cloner le track pour l'enregistrement
          audioTracks.push(audioStream);
        } catch (err) {
          console.warn('Erreur récupération track audio local:', err);
        }
      }

      // Ajouter les tracks audio des utilisateurs distants (cloner)
      remoteUsers.forEach((user) => {
        if (user.audioTrack && user.hasAudio) {
          try {
            const audioStream = user.audioTrack.getMediaStreamTrack();
            audioTracks.push(audioStream);
          } catch (err) {
            console.warn('Erreur récupération track audio distant:', err);
          }
        }
      });

      if (audioTracks.length === 0) {
        setError('Aucun audio disponible pour l\'enregistrement');
        setTimeout(() => setError(''), 5000);
        return;
      }

      // Fonction pour capturer toute la zone de réunion dans le canvas
      const captureFrame = () => {
        if (!canvas || !ctx || !videoContainer) return;

        try {
          // Remplir le canvas avec le fond de la réunion
          ctx.fillStyle = '#0f172a'; // bg-slate-900
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Capturer tous les éléments vidéo visibles dans la zone de réunion
          const videoElements = videoContainer.querySelectorAll('video');
          videoElements.forEach((videoEl) => {
            if (videoEl && videoEl.offsetWidth > 0 && videoEl.offsetHeight > 0 && !videoEl.paused) {
              try {
                const rect = videoEl.getBoundingClientRect();
                const containerRect = videoContainer.getBoundingClientRect();
                
                // Calculer la position relative dans le conteneur
                const x = rect.left - containerRect.left;
                const y = rect.top - containerRect.top;
                
                // Dessiner la vidéo sur le canvas
                ctx.drawImage(videoEl, x, y, rect.width, rect.height);
              } catch (drawError) {
                // Ignorer les erreurs de capture (vidéo pas encore prête)
              }
            }
          });

          // Capturer aussi les écrans partagés
          const screenElements = videoContainer.querySelectorAll('[id^="remote-screen-"]');
          screenElements.forEach((screenEl) => {
            const videoInScreen = screenEl.querySelector('video');
            if (videoInScreen && videoInScreen.offsetWidth > 0 && !videoInScreen.paused) {
              try {
                const rect = screenEl.getBoundingClientRect();
                const containerRect = videoContainer.getBoundingClientRect();
                const x = rect.left - containerRect.left;
                const y = rect.top - containerRect.top;
                ctx.drawImage(videoInScreen, x, y, rect.width, rect.height);
              } catch (drawError) {
                // Ignorer
              }
            }
          });
        } catch (captureError) {
          console.warn('Erreur capture frame:', captureError);
        }
      };

      // Capturer à 30 FPS
      const captureInterval = setInterval(captureFrame, 1000 / 30);
      captureIntervalRef.current = captureInterval; // Stocker la référence

      // Créer un stream depuis le canvas
      const canvasStream = canvas.captureStream(30);
      setCanvasStream(canvasStream);

      // Créer un MediaStream combiné avec le canvas et tous les audios
      const combinedStream = new MediaStream();
      combinedStreamRef.current = combinedStream; // Stocker dans le ref pour y accéder dans onstop
      
      // Ajouter la vidéo du canvas
      canvasStream.getVideoTracks().forEach(track => {
        combinedStream.addTrack(track);
      });

      // IMPORTANT: Ne PAS ajouter les tracks audio directement car cela peut causer une déconnexion
      // Le MediaRecorder peut arrêter les tracks originaux d'Agora même si on les retire après
      // Solution: Enregistrer uniquement la vidéo pour éviter d'affecter la réunion
      // Si l'utilisateur veut l'audio, il faudrait utiliser une autre méthode (AudioContext, etc.)
      // Pour l'instant, on enregistre uniquement la vidéo pour éviter les problèmes de déconnexion
      
      // NOTE: Si vous voulez vraiment enregistrer l'audio, il faudrait créer des clones des tracks
      // ou utiliser un AudioContext pour mixer l'audio sans toucher aux tracks originaux
      // Mais pour l'instant, on évite cela pour préserver la stabilité de la réunion

      // Créer le MediaRecorder
      const options = {
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: 5000000 // 5 Mbps pour une bonne qualité
      };

      // Essayer d'autres codecs si vp9 n'est pas supporté
      let recorder;
      if (MediaRecorder.isTypeSupported(options.mimeType)) {
        recorder = new MediaRecorder(combinedStream, options);
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
        recorder = new MediaRecorder(combinedStream, { mimeType: 'video/webm;codecs=vp8,opus' });
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        recorder = new MediaRecorder(combinedStream, { mimeType: 'video/webm' });
      } else {
        recorder = new MediaRecorder(combinedStream);
      }

      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        console.log('MediaRecorder onstop appelé');
        
        try {
          // Vérifier que le client est toujours connecté avant de nettoyer
          if (client) {
            const connectionState = client.connectionState;
            console.log('État de connexion client dans onstop:', connectionState);
            
            // Si le client est déconnecté, ne pas continuer le nettoyage
            if (connectionState === 'DISCONNECTED' || connectionState === 'FAILED') {
              console.warn('⚠️ Client déconnecté, arrêt du nettoyage pour éviter les erreurs');
              return;
            }
          }
          
          // CRITIQUE: Retirer les tracks audio du combinedStream AVANT d'arrêter quoi que ce soit
          // pour éviter que le navigateur n'affecte les tracks originaux
          if (combinedStreamRef.current) {
            try {
              // Retirer tous les tracks audio du combinedStream
              const audioTracksToRemove = [...combinedStreamRef.current.getAudioTracks()];
              audioTracksToRemove.forEach(track => {
                try {
                  combinedStreamRef.current.removeTrack(track);
                  console.log('Track audio retiré du combinedStream');
                } catch (removeErr) {
                  console.warn('Erreur retrait track audio:', removeErr);
                }
              });
              // Nettoyer la référence
              combinedStreamRef.current = null;
            } catch (streamErr) {
              console.warn('Erreur manipulation combinedStream:', streamErr);
            }
          }
          
          // Arrêter la capture
          if (captureIntervalRef.current) {
            clearInterval(captureIntervalRef.current);
            captureIntervalRef.current = null;
            console.log('Intervalle de capture arrêté dans onstop');
          }
          
          // IMPORTANT: Ne pas arrêter les tracks audio car ils sont utilisés pour la réunion
          // Seul le canvas stream (vidéo) doit être arrêté
          if (canvasStream) {
            canvasStream.getTracks().forEach(track => {
              // Arrêter uniquement les tracks vidéo du canvas
              if (track.kind === 'video') {
                try {
                  track.stop();
                  console.log('Track canvas vidéo arrêté dans onstop');
                } catch (trackErr) {
                  console.warn('Erreur arrêt track canvas vidéo:', trackErr);
                }
              }
              // NE PAS arrêter les tracks audio ici
            });
          }
          // NE PAS arrêter audioTracks car ils sont partagés avec la réunion active
          // Les tracks audio ont été retirés du combinedStream, donc ils ne seront pas affectés

          // Nettoyer le canvas
          if (canvasRef.current) {
            canvasRef.current = null;
          }
          
          // Vérifier à nouveau que le client est toujours connecté après le nettoyage
          if (client) {
            const finalConnectionState = client.connectionState;
            console.log('État de connexion client après nettoyage:', finalConnectionState);
            if (finalConnectionState === 'DISCONNECTED' || finalConnectionState === 'FAILED') {
              console.error('⚠️ Client déconnecté après nettoyage de l\'enregistrement !');
              // Ne pas déconnecter l'utilisateur, juste logger l'erreur
            }
          }
        } catch (cleanupErr) {
          console.error('Erreur lors du nettoyage dans onstop:', cleanupErr);
          // Ne pas propager l'erreur pour éviter de déconnecter l'utilisateur
          // Vérifier que le client est toujours connecté
          if (client) {
            const connectionState = client.connectionState;
            if (connectionState === 'DISCONNECTED' || connectionState === 'FAILED') {
              console.error('⚠️ Client déconnecté après erreur de nettoyage !');
            }
          }
        }

        // Créer le blob
        const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
        
        // Méthode améliorée pour le téléchargement (compatible Chrome, Edge, Firefox)
        try {
          const url = URL.createObjectURL(blob);
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const filename = `recording-${channelName || 'meeting'}-${timestamp}.webm`;
          
          // Méthode 1: Essayer avec un lien temporaire
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.style.display = 'none';
          
          // Ajouter au DOM
          document.body.appendChild(a);
          
          // Déclencher le téléchargement
          // Utiliser requestAnimationFrame pour s'assurer que le DOM est prêt
          requestAnimationFrame(() => {
            a.click();
            
            // Nettoyer après un court délai
            setTimeout(() => {
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              console.log('Enregistrement téléchargé:', filename);
              setError('✅ Enregistrement téléchargé avec succès !');
              setTimeout(() => setError(''), 5000);
            }, 100);
          });
          
        } catch (downloadError) {
          console.error('Erreur téléchargement:', downloadError);
          
          // Méthode alternative: Ouvrir dans un nouvel onglet (fallback)
          try {
            const url = URL.createObjectURL(blob);
            const newWindow = window.open(url, '_blank');
            if (!newWindow) {
              // Si popup bloquée, afficher un message avec le lien
              setError('⚠️ Téléchargement bloqué. Cliquez ici pour télécharger manuellement.');
              setTimeout(() => {
                const manualLink = document.createElement('a');
                manualLink.href = url;
                manualLink.download = `recording-${channelName || 'meeting'}-${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;
                manualLink.click();
                URL.revokeObjectURL(url);
              }, 1000);
            } else {
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
          } catch (fallbackError) {
            console.error('Erreur méthode alternative:', fallbackError);
            setError('❌ Erreur lors du téléchargement. Vérifiez les paramètres de votre navigateur.');
            setTimeout(() => setError(''), 5000);
          }
        }
        
        setRecordedChunks([]);
        setCanvasStream(null);
      };

      recorder.onerror = (event) => {
        console.error('Erreur MediaRecorder:', event);
        if (captureIntervalRef.current) {
          clearInterval(captureIntervalRef.current);
          captureIntervalRef.current = null;
        }
        setError('Erreur lors de l\'enregistrement: ' + event.error?.message);
        setTimeout(() => setError(''), 5000);
        setIsRecording(false);
        setMediaRecorder(null);
        if (canvasStream) {
          canvasStream.getTracks().forEach(track => track.stop());
        }
        setCanvasStream(null);
      };

      // Démarrer l'enregistrement
      recorder.start(1000); // Enregistrer par chunks de 1 seconde
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordedChunks(chunks);
      console.log('Enregistrement de la zone de réunion démarré (sans notification de partage)');
      
      setError('✅ Enregistrement démarré ! Capture de la zone de réunion (pas de notification de partage).');
      setTimeout(() => setError(''), 5000);

    } catch (err) {
      console.error('Erreur démarrage enregistrement:', err);
      setError('Impossible de démarrer l\'enregistrement: ' + err.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  // Arrêter l'enregistrement local
  const stopLocalRecording = async () => {
    console.log('Arrêt de l\'enregistrement demandé...');
    console.log('mediaRecorder:', mediaRecorder);
    console.log('isRecording:', isRecording);
    
    // Vérifier que le client est toujours connecté avant d'arrêter
    if (client) {
      const connectionState = client.connectionState;
      console.log('État de connexion client avant arrêt enregistrement:', connectionState);
      
      if (connectionState === 'DISCONNECTED' || connectionState === 'FAILED') {
        console.warn('⚠️ Client déjà déconnecté, arrêt de l\'enregistrement sans nettoyage');
        setIsRecording(false);
        setMediaRecorder(null);
        setRecordedChunks([]);
        return;
      }
    }
    
    try {
      // CRITIQUE: Retirer les tracks audio AVANT d'arrêter le MediaRecorder
      // pour éviter que le navigateur n'affecte les tracks originaux d'Agora
      if (combinedStreamRef.current) {
        try {
          // Retirer tous les tracks audio du combinedStream AVANT d'arrêter
          const audioTracksToRemove = [...combinedStreamRef.current.getAudioTracks()];
          console.log('Retrait de', audioTracksToRemove.length, 'tracks audio du combinedStream');
          audioTracksToRemove.forEach(track => {
            try {
              combinedStreamRef.current.removeTrack(track);
              console.log('Track audio retiré du combinedStream dans stopLocalRecording');
            } catch (removeErr) {
              console.warn('Erreur retrait track audio:', removeErr);
            }
          });
          // Attendre un peu pour que le navigateur traite le retrait
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (streamErr) {
          console.warn('Erreur manipulation combinedStream:', streamErr);
        }
      }
      
      // Arrêter l'intervalle de capture
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = null;
        console.log('Intervalle de capture arrêté');
      }
      
      // Arrêter le MediaRecorder si actif (maintenant que les tracks audio sont retirés)
      if (mediaRecorder) {
        try {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
            console.log('MediaRecorder arrêté');
          } else if (mediaRecorder.state === 'paused') {
            mediaRecorder.stop();
            console.log('MediaRecorder (paused) arrêté');
          } else {
            console.log('MediaRecorder déjà inactif:', mediaRecorder.state);
          }
        } catch (err) {
          console.error('Erreur arrêt MediaRecorder:', err);
        }
      }
      
      // Arrêter uniquement le stream canvas (vidéo)
      // IMPORTANT: Ne pas arrêter les tracks audio car ils sont utilisés pour la réunion
      if (canvasStream) {
        try {
          canvasStream.getTracks().forEach(track => {
            // Arrêter uniquement les tracks vidéo du canvas
            if (track.kind === 'video') {
              track.stop();
              console.log('Track canvas vidéo arrêté');
            }
            // Ne pas arrêter les tracks audio ici
          });
          setCanvasStream(null);
        } catch (err) {
          console.error('Erreur arrêt canvasStream:', err);
        }
      }
      
      // Nettoyer le canvas
      if (canvasRef.current) {
        canvasRef.current = null;
      }
      
      // Réinitialiser les états
      setIsRecording(false);
      setMediaRecorder(null);
      setRecordedChunks([]);
      
      // Vérifier que le client est toujours connecté après l'arrêt
      if (client) {
        const finalConnectionState = client.connectionState;
        console.log('État de connexion client après arrêt enregistrement:', finalConnectionState);
        if (finalConnectionState === 'DISCONNECTED' || finalConnectionState === 'FAILED') {
          console.error('⚠️ Client déconnecté après arrêt de l\'enregistrement !');
          setError('⚠️ Connexion perdue après l\'arrêt de l\'enregistrement. Veuillez rafraîchir la page.');
          setTimeout(() => setError(''), 10000);
        }
      }
      
      console.log('Enregistrement arrêté avec succès');
    } catch (err) {
      console.error('Erreur lors de l\'arrêt de l\'enregistrement:', err);
      // Même en cas d'erreur, réinitialiser les états pour éviter de rester bloqué
      setIsRecording(false);
      setMediaRecorder(null);
      setRecordedChunks([]);
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = null;
      }
      
      // Vérifier l'état de connexion après l'erreur
      if (client) {
        const connectionState = client.connectionState;
        if (connectionState === 'DISCONNECTED' || connectionState === 'FAILED') {
          console.error('⚠️ Client déconnecté après erreur d\'arrêt enregistrement !');
          setError('⚠️ Connexion perdue. Veuillez rafraîchir la page.');
          setTimeout(() => setError(''), 10000);
        } else {
          setError('Erreur lors de l\'arrêt de l\'enregistrement, mais l\'enregistrement a été arrêté.');
          setTimeout(() => setError(''), 5000);
        }
      } else {
        setError('Erreur lors de l\'arrêt de l\'enregistrement, mais l\'enregistrement a été arrêté.');
        setTimeout(() => setError(''), 5000);
      }
    }
  };

  // Toggle screen sharing
  const toggleScreenShare = async () => {
    if (!client) return;

    try {
      if (isScreenSharing && screenTrack) {
        // Arrêter le partage d'écran
        // Vérifier que le client est connecté avant d'unpublish
        try {
          // Vérifier l'état de connexion du client
          const connectionState = client.connectionState;
          console.log('État de connexion client:', connectionState);
          
          if (connectionState === 'CONNECTED' || connectionState === 'CONNECTING') {
            await client.unpublish([screenTrack]);
            console.log('Screen track dépublié avec succès');
          } else {
            console.warn('Client non connecté, arrêt du track sans unpublish');
          }
        } catch (unpublishError) {
          // Si l'erreur est INVALID_OPERATION, le client n'est pas connecté
          if (unpublishError.message && unpublishError.message.includes('INVALID_OPERATION')) {
            console.warn('Client non connecté, arrêt du track sans unpublish');
          } else {
            throw unpublishError;
          }
        }
        
        screenTrack.stop();
        screenTrack.close();
        setScreenTrack(null);
        setIsScreenSharing(false);
        console.log('Partage d\'écran arrêté');
        
        // Réactiver la caméra si elle était active avant
        if (localVideoTrack && isVideoEnabled) {
          try {
            // Utiliser la fonction sécurisée pour publier
            await safePublishVideoTrack(localVideoTrack);
            console.log('✅ Caméra réactivée après arrêt du partage d\'écran');
            
            // Réafficher la vidéo locale
            if (localVideoRef.current) {
              localVideoRef.current.style.display = 'block';
              await safePlay(localVideoTrack, localVideoRef.current, 'local-video');
            }
          } catch (publishError) {
            console.warn('Erreur réactivation caméra:', publishError);
            if (publishError.message && publishError.message.includes('CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS')) {
              setError('⚠️ Impossible de réactiver la caméra: un track vidéo est encore actif. Veuillez réessayer dans quelques secondes.');
            } else {
              setError('Erreur lors de la réactivation de la caméra. Essayez de la réactiver manuellement.');
            }
            setTimeout(() => setError(''), 5000);
          }
        }
      } else {
        // IMPORTANT: Agora ne permet pas de publier plusieurs tracks vidéo simultanément
        // Il faut désactiver la caméra avant de partager l'écran
        
        // Sauvegarder l'état de la caméra
        const wasVideoEnabled = isVideoEnabled && localVideoTrack;
        
        // Démarrer le partage d'écran
        const screenVideoTrack = await AgoraRTC.createScreenVideoTrack({
          encoderConfig: '1080p_1', // Qualité HD
          optimizationMode: 'detail' // Optimisé pour la qualité
        });
        
        setScreenTrack(screenVideoTrack);
        setIsScreenSharing(true);
        
        // Désactiver/dépublicher la caméra locale si elle est active (AVANT de publier l'écran)
        if (localVideoTrack && client) {
          try {
            await unpublishVideoTrackAndWait(localVideoTrack);
            console.log('✅ Caméra désactivée temporairement pour le partage d\'écran');
          } catch (unpublishError) {
            console.warn('Erreur dépublication caméra:', unpublishError);
            // Continuer quand même, safePublishVideoTrack gérera cela
          }
        }
        
        // Publier le track d'écran avec la fonction sécurisée
        await safePublishVideoTrack(screenVideoTrack);
        console.log('✅ Partage d\'écran démarré');
        
        // Afficher l'écran partagé localement si un élément est disponible
        if (screenShareRef.current) {
          await safePlay(screenVideoTrack, screenShareRef.current, 'local-screen');
        }
        
        // Gérer l'arrêt automatique si l'utilisateur arrête le partage depuis le navigateur
        screenVideoTrack.on('track-ended', async () => {
          console.log('Partage d\'écran arrêté par l\'utilisateur');
          setIsScreenSharing(false);
          setScreenTrack(null);
          
          // Réactiver la caméra si elle était active avant
          if (wasVideoEnabled && localVideoTrack && client) {
            try {
              // Utiliser la fonction sécurisée pour publier
              await safePublishVideoTrack(localVideoTrack);
              console.log('✅ Caméra réactivée après arrêt automatique du partage d\'écran');
              
              // Réafficher la vidéo locale
              if (localVideoRef.current) {
                localVideoRef.current.style.display = 'block';
                await safePlay(localVideoTrack, localVideoRef.current, 'local-video');
              }
            } catch (publishError) {
              console.warn('Erreur réactivation caméra:', publishError);
              if (publishError.message && publishError.message.includes('CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS')) {
                console.warn('⚠️ Impossible de réactiver la caméra automatiquement, l\'utilisateur devra le faire manuellement');
              }
            }
          }
        });
      }
    } catch (err) {
      console.error('Erreur partage d\'écran:', err);
      if (err.message.includes('NotAllowedError') || err.message.includes('PERMISSION_DENIED')) {
        setError('Permission refusée. Veuillez autoriser le partage d\'écran dans les paramètres de votre navigateur.');
      } else if (err.message.includes('NotReadableError')) {
        setError('Impossible d\'accéder à l\'écran. Vérifiez que vous avez sélectionné une fenêtre/onglet à partager.');
      } else if (err.message.includes('CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS')) {
        setError('⚠️ Erreur: Impossible de partager l\'écran et la caméra en même temps. Tentative de correction automatique...');
        // Réessayer après avoir désactivé la caméra avec la fonction sécurisée
        if (localVideoTrack && client) {
          try {
            await unpublishVideoTrackAndWait(localVideoTrack);
            // Attendre un peu plus pour être sûr
            await new Promise(resolve => setTimeout(resolve, 500));
            // Réessayer le partage d'écran
            setTimeout(() => {
              toggleScreenShare();
            }, 300);
          } catch (unpublishError) {
            console.error('Erreur dépublication caméra:', unpublishError);
            setError('❌ Erreur lors de la correction automatique. Veuillez arrêter manuellement la caméra puis réessayer le partage d\'écran.');
            setTimeout(() => setError(''), 7000);
          }
        }
      } else {
        setError('Erreur lors du partage d\'écran: ' + err.message);
      }
      setTimeout(() => setError(''), 5000);
    }
  };

  // Rejoindre une réunion existante
  const joinExistingMeeting = async () => {
    if (!displayName.trim()) {
      setError('Veuillez entrer votre nom');
      return;
    }

    if (!channelName.trim()) {
      setError('Veuillez entrer le nom de la réunion');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Vous devez être connecté pour rejoindre une réunion');
      }

      // Appeler l'API backend pour obtenir le token Agora avec le channel existant
      const response = await axios.post(
        `${API_BASE}/agora/token`,
        { channelName: channelName },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setAgoraConfig(response.data);
        // Stocker le nom local depuis le backend pour référence
        if (response.data.userName) {
          console.log('Nom utilisateur depuis backend:', response.data.userName);
        }
        await joinChannel(response.data);
      } else {
        throw new Error(response.data.message || 'Erreur lors de la connexion à la réunion');
      }
    } catch (err) {
      console.error('Erreur connexion réunion:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Impossible de rejoindre la réunion';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si un room est passé en paramètre URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roomParam = urlParams.get('room');
    if (roomParam && !inMeeting) {
      setChannelName(roomParam);
      // Optionnel : auto-rejoindre si l'utilisateur a déjà un nom
      // Vous pouvez décommenter la ligne suivante si vous voulez auto-rejoindre
      // if (displayName) {
      //   joinExistingMeeting();
      // }
    }
  }, [displayName]);

  // Nettoyer au démontage du composant uniquement (pas quand mediaRecorder/canvasStream changent)
  useEffect(() => {
    return () => {
      // Ce cleanup ne s'exécute QUE lors du démontage du composant
      // Ne pas nettoyer ici car cela causerait une déconnexion lors de l'arrêt de l'enregistrement
      // Le nettoyage de l'enregistrement est géré dans stopLocalRecording et recorder.onstop
      console.log('Composant démonté, nettoyage final...');
      
      // Arrêter l'enregistrement si actif
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        try {
          mediaRecorder.stop();
        } catch (e) {
          console.warn('Erreur arrêt MediaRecorder au démontage:', e);
        }
      }
      // Arrêter le stream d'écran si présent
      if (canvasStream) {
        try {
          canvasStream.getTracks().forEach(track => {
            if (track.kind === 'video') {
              track.stop();
            }
          });
        } catch (e) {
          console.warn('Erreur arrêt canvasStream au démontage:', e);
        }
      }
      if (localAudioTrack) {
        try {
          localAudioTrack.stop();
          localAudioTrack.close();
        } catch (e) {
          console.warn('Erreur nettoyage audio au démontage:', e);
        }
      }
      if (localVideoTrack) {
        try {
          localVideoTrack.stop();
          localVideoTrack.close();
        } catch (e) {
          console.warn('Erreur nettoyage vidéo au démontage:', e);
        }
      }
      if (client) {
        try {
          client.leave();
        } catch (e) {
          console.warn('Erreur leave client au démontage:', e);
        }
      }
    };
    // IMPORTANT: Ne pas mettre mediaRecorder ou canvasStream comme dépendances
    // car cela déclencherait le cleanup lors de l'arrêt de l'enregistrement
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dépendances vides = s'exécute uniquement au montage/démontage

  // Référence pour éviter les appels concurrents à play()
  const playingTracksRef = useRef(new Set());
  
  // Référence pour éviter les publications concurrentes de tracks vidéo (mutex)
  const isPublishingVideoRef = useRef(false);

  // Fonction helper pour dépublicher un track vidéo et attendre la confirmation
  const unpublishVideoTrackAndWait = async (track) => {
    if (!client || !track) return;
    
    try {
      const connectionState = client.connectionState;
      if (connectionState !== 'CONNECTED' && connectionState !== 'CONNECTING') {
        console.warn('Client non connecté, skip unpublish');
        return;
      }
      
      // Dépublicher le track
      await client.unpublish([track]);
      console.log('✅ Track vidéo dépublié');
      
      // Attendre que le track soit vraiment dépublié (vérification avec retry)
      let retries = 0;
      const maxRetries = 20; // 2 secondes max
      
      while (retries < maxRetries) {
        // Vérifier les tracks publiés localement
        const localTracks = client.localTracks || [];
        const publishedVideoTracks = localTracks.filter(t => 
          t && t.trackMediaType === 'video' && t.isPlaying
        );
        
        // Si le track n'est plus dans la liste des tracks publiés, c'est bon
        if (!publishedVideoTracks.includes(track)) {
          console.log('✅ Track vidéo confirmé comme dépublié après', retries * 100, 'ms');
          // Attendre encore un peu pour être sûr
          await new Promise(resolve => setTimeout(resolve, 200));
          return;
        }
        
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }
      
      console.warn('⚠️ Timeout: Le track n\'a pas été confirmé comme dépublié dans les temps');
    } catch (err) {
      // Si l'erreur est INVALID_OPERATION, le client n'est pas connecté, c'est OK
      if (err.message && err.message.includes('INVALID_OPERATION')) {
        console.warn('Client non connecté, skip unpublish');
        return;
      }
      throw err;
    }
  };

  // Fonction helper pour publier un track vidéo de manière sécurisée (avec mutex)
  const safePublishVideoTrack = async (track) => {
    if (!client || !track) {
      throw new Error('Client ou track manquant');
    }
    
    // Vérifier le mutex
    if (isPublishingVideoRef.current) {
      console.log('⏳ Publication vidéo en cours, attente...');
      // Attendre que la publication précédente soit terminée
      let waitRetries = 0;
      while (isPublishingVideoRef.current && waitRetries < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        waitRetries++;
      }
      if (isPublishingVideoRef.current) {
        throw new Error('Timeout: Publication vidéo précédente n\'a pas terminé');
      }
    }
    
    // Activer le mutex
    isPublishingVideoRef.current = true;
    
    try {
      // Vérifier l'état de connexion
      const connectionState = client.connectionState;
      if (connectionState !== 'CONNECTED' && connectionState !== 'CONNECTING') {
        throw new Error('Client non connecté');
      }
      
      // ÉTAPE 1: Dépublicher tous les tracks vidéo existants
      const localTracks = client.localTracks || [];
      const videoTracks = localTracks.filter(t => 
        t && t.trackMediaType === 'video'
      );
      
      console.log('📹 Tracks vidéo existants à dépublicher:', videoTracks.length);
      
      for (const videoTrack of videoTracks) {
        try {
          await unpublishVideoTrackAndWait(videoTrack);
        } catch (unpublishErr) {
          // Ignorer les erreurs si le track est déjà dépublié
          if (!unpublishErr.message || !unpublishErr.message.includes('INVALID_OPERATION')) {
            console.warn('Erreur dépublication track vidéo:', unpublishErr);
          }
        }
      }
      
      // ÉTAPE 2: Attendre un peu pour être sûr que tout est dépublié
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // ÉTAPE 3: Vérifier qu'aucun track vidéo n'est encore publié
      const remainingVideoTracks = (client.localTracks || []).filter(t => 
        t && t.trackMediaType === 'video' && t.isPlaying
      );
      
      if (remainingVideoTracks.length > 0) {
        console.warn('⚠️ Des tracks vidéo sont encore publiés, nouvelle tentative...');
        // Réessayer une fois
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // ÉTAPE 4: Publier le nouveau track
      await client.publish([track]);
      console.log('✅ Track vidéo publié avec succès');
      
    } finally {
      // Désactiver le mutex
      isPublishingVideoRef.current = false;
    }
  };

  // Fonction helper pour obtenir les caméras de manière sécurisée
  const safeGetCameras = async () => {
    try {
      const cameras = await AgoraRTC.getCameras();
      return cameras;
    } catch (err) {
      // Si erreur de permission, retourner un tableau vide
      if (err.message && (err.message.includes('PERMISSION_DENIED') || err.message.includes('Permission denied'))) {
        console.warn('Permissions caméra non accordées, utilisation de la caméra par défaut');
        return [];
      }
      throw err;
    }
  };

  // Fonction helper pour créer un track vidéo de manière sécurisée
  const safeCreateVideoTrack = async (cameraId = null) => {
    try {
      if (cameraId) {
        return await AgoraRTC.createCameraVideoTrack({ cameraId });
      } else {
        return await AgoraRTC.createCameraVideoTrack();
      }
    } catch (err) {
      // Si erreur de permission, essayer sans spécifier de caméra
      if (err.message && (err.message.includes('PERMISSION_DENIED') || err.message.includes('Permission denied'))) {
        throw new Error('Permission refusée. Veuillez autoriser l\'accès à la caméra dans les paramètres de votre navigateur.');
      }
      throw err;
    }
  };

  // Fonction helper pour jouer un track de manière sécurisée
  const safePlay = async (track, element, trackId) => {
    // Vérifier si ce track est déjà en cours de lecture
    if (playingTracksRef.current.has(trackId)) {
      console.log(`⏸️ Track ${trackId} déjà en cours de lecture, skip`);
      return;
    }

    try {
      // Marquer comme en cours
      playingTracksRef.current.add(trackId);
      
      // Arrêter le track s'il est déjà joué ailleurs
      try {
        track.stop();
        await new Promise(resolve => setTimeout(resolve, 50));
      } catch (stopErr) {
        // Ignorer les erreurs d'arrêt si le track n'était pas joué
      }

      // Jouer le track
      await track.play(element);
      console.log(`✅ Track ${trackId} joué avec succès`);
    } catch (error) {
      // Gérer l'erreur "interrupted"
      if (error.message && error.message.includes('interrupted')) {
        console.warn(`⚠️ Lecture ${trackId} interrompue, réessai...`);
        // Réessayer après un court délai
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
          track.stop();
          await new Promise(resolve => setTimeout(resolve, 50));
          await track.play(element);
          console.log(`✅ Track ${trackId} rejoué après interruption`);
        } catch (retryError) {
          console.warn(`⚠️ Erreur réessai track ${trackId}:`, retryError);
        }
      } else {
        console.warn(`⚠️ Erreur lecture track ${trackId}:`, error);
      }
    } finally {
      // Retirer du set après un court délai pour permettre les mises à jour
      setTimeout(() => {
        playingTracksRef.current.delete(trackId);
      }, 200);
    }
  };

  // Afficher la vidéo locale quand elle est disponible
  useEffect(() => {
    if (localVideoTrack && isVideoEnabled) {
      const playVideo = async () => {
        // Attendre que le ref soit disponible
        let retries = 0;
        const maxRetries = 20; // Augmenter pour Chrome
        
        while (!localVideoRef.current && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }
        
        if (localVideoRef.current) {
          // S'assurer que l'élément est visible dans Chrome
          localVideoRef.current.style.display = 'block';
          
          // Forcer un reflow pour Chrome (nécessaire pour certains navigateurs)
          if (localVideoRef.current.offsetHeight === 0) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          
          // Vérifier que l'élément est toujours dans le DOM
          if (document.body.contains(localVideoRef.current)) {
            await safePlay(localVideoTrack, localVideoRef.current, 'local-video');
            
            // Vérifier après un court délai que la vidéo est bien créée (spécifique Chrome)
            setTimeout(() => {
              if (localVideoRef.current) {
                const videoElement = localVideoRef.current.querySelector('video');
                if (!videoElement || videoElement.paused) {
                  console.warn('⚠️ Vidéo locale non visible dans Chrome, réessai...');
                  // Réessayer une fois
                  safePlay(localVideoTrack, localVideoRef.current, 'local-video').catch(err => {
                    console.error('Erreur réessai vidéo locale:', err);
                  });
                }
              }
            }, 300);
          } else {
            console.warn('⚠️ localVideoRef.current n\'est plus dans le DOM');
          }
        } else {
          console.warn('Ref vidéo locale non disponible après', maxRetries, 'tentatives');
        }
      };
      playVideo();
    } else if (localVideoTrack && !isVideoEnabled && localVideoRef.current) {
      // Arrêter la vidéo si elle est désactivée
      try {
        localVideoTrack.stop();
        playingTracksRef.current.delete('local-video');
      } catch (error) {
        console.warn('Erreur arrêt vidéo locale:', error);
      }
    }
  }, [localVideoTrack, isVideoEnabled]);

  // Afficher les vidéos distantes avec identification
  useEffect(() => {
    remoteUsers.forEach((user) => {
      if (user.videoTrack) {
        const videoElementId = `remote-video-${user.uid}`;
        const trackId = `remote-video-${user.uid}`;
        const playVideo = async () => {
          let retries = 0;
          const maxRetries = 15;
          
          // Attendre que l'élément soit disponible
          while (retries < maxRetries) {
            const element = document.getElementById(videoElementId);
            if (element) {
              // Vérifier si le track est déjà joué sur cet élément
              const previousElement = remoteVideosRef.current[user.uid];
              
              // Si l'élément a changé, mettre à jour la référence
              if (previousElement !== element) {
                remoteVideosRef.current[user.uid] = element;
              }
              
              // Jouer le track de manière sécurisée
              await safePlay(user.videoTrack, element, trackId);
              return;
            } else {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            retries++;
          }
          console.warn('❌ Impossible d\'afficher la vidéo distante pour user', user.uid, 'après', maxRetries, 'tentatives');
        };
        playVideo();
      } else {
        // Nettoyer si la vidéo n'est plus disponible
        if (remoteVideosRef.current[user.uid]) {
          delete remoteVideosRef.current[user.uid];
        }
        playingTracksRef.current.delete(`remote-video-${user.uid}`);
      }
    });

    // Nettoyer les vidéos des utilisateurs qui ont quitté
    const currentUids = remoteUsers.map(u => u.uid.toString());
    Object.keys(remoteVideosRef.current).forEach((uid) => {
      if (!currentUids.includes(uid)) {
        delete remoteVideosRef.current[uid];
        playingTracksRef.current.delete(`remote-video-${uid}`);
      }
    });
  }, [remoteUsers, selectedStreamForMainView]); // Ajouter selectedStreamForMainView comme dépendance

  // Référence pour stocker les éléments où les écrans partagés sont joués
  const remoteScreenElementsRef = useRef({});

  // Référence pour stocker l'élément où l'écran partagé local est joué
  const localScreenElementRef = useRef(null);

  // Afficher l'écran partagé local
  useEffect(() => {
    if (isScreenSharing && screenTrack) {
      const playLocalScreen = async () => {
        let retries = 0;
        const maxRetries = 20;
        
        while (retries < maxRetries) {
          // Attendre que l'élément soit disponible dans le DOM
          if (screenShareRef.current) {
            // Mettre à jour la référence de l'élément
            localScreenElementRef.current = screenShareRef.current;
            
            // Jouer le track de manière sécurisée
            await safePlay(screenTrack, screenShareRef.current, 'local-screen');
            return;
          } else {
            // Attendre que l'élément soit disponible
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          retries++;
        }
        console.warn('❌ Impossible d\'afficher l\'écran partagé local après', maxRetries, 'tentatives');
      };
      playLocalScreen();
    } else if (!isScreenSharing) {
      // Nettoyer la référence quand le partage d'écran s'arrête
      localScreenElementRef.current = null;
      playingTracksRef.current.delete('local-screen');
    }
  }, [isScreenSharing, screenTrack, selectedStreamForMainView]); // Ajouter selectedStreamForMainView pour rejouer si nécessaire

  // Afficher les écrans partagés distants
  useEffect(() => {
    Object.keys(remoteScreenShares).forEach((uid) => {
      const screenTrack = remoteScreenShares[uid];
      const elementId = `remote-screen-${uid}`;
      const trackId = `remote-screen-${uid}`;
      const playScreen = async () => {
        let retries = 0;
        const maxRetries = 15;
        
        while (retries < maxRetries) {
          const element = document.getElementById(elementId);
          if (element) {
            // Mettre à jour la référence de l'élément
            remoteScreenElementsRef.current[uid] = element;
            
            // Jouer le track de manière sécurisée
            await safePlay(screenTrack, element, trackId);
            return;
          } else {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          retries++;
        }
        console.warn('❌ Impossible d\'afficher l\'écran partagé pour UID', uid, 'après', maxRetries, 'tentatives');
      };
      playScreen();
    });
    
    // Nettoyer les références pour les écrans partagés qui n'existent plus
    Object.keys(remoteScreenElementsRef.current).forEach((uid) => {
      if (!remoteScreenShares[uid]) {
        delete remoteScreenElementsRef.current[uid];
        playingTracksRef.current.delete(`remote-screen-${uid}`);
      }
    });
  }, [remoteScreenShares, selectedStreamForMainView]); // Ajouter selectedStreamForMainView comme dépendance

  if (inMeeting) {
    return (
      <div className="relative min-h-screen bg-slate-900">
        {/* Header minimaliste en haut */}
        <div className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/60 to-transparent backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-gray-300" />
                <span className="text-sm font-medium text-gray-200">Réunion en cours</span>
              </div>
              <div className="h-4 w-px bg-gray-600" />
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Channel:</span>
                <code className="text-xs font-mono text-gray-300 bg-gray-800/50 px-2 py-0.5 rounded">
                  {channelName}
                </code>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-xs text-gray-400">{remoteUsers.length + 1} participant(s)</span>
              </div>
              {isRecording && (
                <div className="flex items-center gap-2 bg-red-500/20 px-2 py-1 rounded-full border border-red-500/30">
                  <Circle className="h-2.5 w-2.5 text-red-500 fill-red-500 animate-pulse" />
                  <span className="text-xs text-red-400 font-medium">Enregistrement</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Barre de contrôles en bas (style professionnel) */}
        <div className="absolute bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-black/95 via-black/90 to-transparent backdrop-blur-md border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-center gap-2">
              {/* Bouton Microphone */}
              <button
                onClick={toggleAudio}
                className={`group relative flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200 ${
                  isAudioEnabled 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
                title={isAudioEnabled ? 'Couper le micro' : 'Activer le micro'}
              >
                {isAudioEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
              </button>
              
              {/* Bouton Caméra */}
              <button
                onClick={localVideoTrack ? toggleVideo : async () => {
                  try {
                    // Essayer d'obtenir les caméras disponibles (peut échouer si pas de permissions)
                    let cameras = [];
                    try {
                      cameras = await safeGetCameras();
                      setAvailableCameras(cameras);
                    } catch (camerasErr) {
                      console.warn('Impossible d\'obtenir la liste des caméras:', camerasErr);
                    }
                    
                    let videoTrack = null;
                    let cameraUsed = false;
                    
                    // Si plusieurs caméras sont disponibles, essayer chacune
                    if (cameras.length > 1) {
                      for (const camera of cameras) {
                        try {
                          videoTrack = await safeCreateVideoTrack(camera.deviceId);
                          setSelectedCameraId(camera.deviceId);
                          cameraUsed = true;
                          break;
                        } catch (err) {
                          console.log(`Caméra ${camera.label} non disponible, essai suivant...`);
                          continue;
                        }
                      }
                    }
                    
                    // Si aucune caméra spécifique n'a fonctionné, essayer la caméra par défaut
                    if (!cameraUsed) {
                      videoTrack = await safeCreateVideoTrack();
                    }
                    
                    setLocalVideoTrack(videoTrack);
                    if (client) {
                      const connectionState = client.connectionState;
                      if (connectionState === 'CONNECTED' || connectionState === 'CONNECTING') {
                        await safePublishVideoTrack(videoTrack);
                      }
                      // Afficher la vidéo après publication
                      if (localVideoRef.current) {
                        localVideoRef.current.style.display = 'block';
                        // Forcer un reflow pour Chrome
                        await new Promise(resolve => setTimeout(resolve, 50));
                        await safePlay(videoTrack, localVideoRef.current, 'local-video');
                        
                        // Vérification spécifique Chrome après un délai
                        setTimeout(() => {
                          if (localVideoRef.current) {
                            const videoEl = localVideoRef.current.querySelector('video');
                            if (!videoEl || videoEl.paused) {
                              console.warn('⚠️ Chrome: Réessai affichage vidéo locale');
                              safePlay(videoTrack, localVideoRef.current, 'local-video').catch(console.error);
                            }
                          }
                        }, 300);
                      }
                    }
                    setIsVideoEnabled(true);
                    setError('');
                  } catch (err) {
                    console.error('Erreur activation caméra:', err);
                    let errorMsg = '';
                    if (err.message && (err.message.includes('Permission denied') || err.message.includes('PERMISSION_DENIED'))) {
                      errorMsg = 'Permission refusée. Cliquez sur l\'icône de cadenas dans la barre d\'adresse et autorisez la caméra.';
                    } else if (err.message && (err.message.includes('NotReadableError') || err.message.includes('NOT_READABLE'))) {
                      // Essayer avec la caméra par défaut sans lister les caméras
                      try {
                        const videoTrack = await safeCreateVideoTrack();
                        setLocalVideoTrack(videoTrack);
                        if (client) {
                          const connectionState = client.connectionState;
                          if (connectionState === 'CONNECTED' || connectionState === 'CONNECTING') {
                            await safePublishVideoTrack(videoTrack);
                            if (localVideoRef.current) {
                              localVideoRef.current.style.display = 'block';
                              // Forcer un reflow pour Chrome
                              await new Promise(resolve => setTimeout(resolve, 50));
                              await safePlay(videoTrack, localVideoRef.current, 'local-video');
                              
                              // Vérification spécifique Chrome
                              setTimeout(() => {
                                if (localVideoRef.current) {
                                  const videoEl = localVideoRef.current.querySelector('video');
                                  if (!videoEl || videoEl.paused) {
                                    console.warn('⚠️ Chrome: Réessai affichage vidéo locale');
                                    safePlay(videoTrack, localVideoRef.current, 'local-video').catch(console.error);
                                  }
                                }
                              }, 300);
                            }
                          }
                        }
                        setIsVideoEnabled(true);
                        setError('');
                        return;
                      } catch (retryErr) {
                        errorMsg = 'La caméra est déjà utilisée par un autre navigateur ou application. Fermez les autres onglets/applications vidéo ou continuez en mode audio seulement.';
                      }
                    } else if (err.message && (err.message.includes('NotFoundError') || err.message.includes('DEVICE_NOT_FOUND'))) {
                      errorMsg = 'Aucune caméra détectée. Vérifiez que votre caméra est connectée.';
                    } else if (err.message && err.message.includes('CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS')) {
                      errorMsg = '⚠️ Impossible d\'activer la caméra: un partage d\'écran est actif. Arrêtez d\'abord le partage d\'écran.';
                    } else {
                      errorMsg = 'Impossible d\'activer la caméra: ' + (err.message || 'Erreur inconnue');
                    }
                    setError(errorMsg);
                    setTimeout(() => setError(''), 5000);
                  }
                }}
                className={`group relative flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200 ${
                  isVideoEnabled && localVideoTrack
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
                title={!localVideoTrack ? 'Cliquez pour activer la caméra' : (isVideoEnabled ? 'Couper la caméra' : 'Activer la caméra')}
              >
                {isVideoEnabled && localVideoTrack ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
              </button>
              
              {/* Bouton Partage d'écran */}
              <button
                onClick={toggleScreenShare}
                className={`group relative flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200 ${
                  isScreenSharing
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-white'
                }`}
                title={isScreenSharing ? 'Arrêter le partage d\'écran' : 'Partager l\'écran'}
              >
                {isScreenSharing ? <MonitorOff className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
              </button>
              
              {/* Bouton Enregistrement */}
              <button
                onClick={isRecording ? stopLocalRecording : startLocalRecording}
                disabled={loading}
                className={`group relative flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200 ${
                  isRecording
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-white'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isRecording ? 'Arrêter l\'enregistrement' : 'Démarrer l\'enregistrement'}
              >
                {isRecording ? (
                  <Square className="h-5 w-5" />
                ) : (
                  <Circle className="h-5 w-5 fill-white" />
                )}
              </button>
              
              {/* Séparateur */}
              <div className="h-8 w-px bg-gray-600 mx-2" />
              
              {/* Bouton Chat */}
              <button
                onClick={() => {
                  chat.setChatOpen(!chat.chatOpen);
                }}
                className="group relative flex items-center justify-center w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-all duration-200"
                title="Ouvrir/Fermer le chat"
              >
                <MessageSquare className="h-5 w-5" />
                {chat.unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {chat.unreadCount > 9 ? '9+' : chat.unreadCount}
                  </span>
                )}
              </button>
              
              {/* Bouton Partager */}
              <button
                onClick={copyRoomLink}
                className="group relative flex items-center justify-center w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-all duration-200"
                title="Partager le lien de la réunion"
              >
                <Share2 className="h-5 w-5" />
                {copied && (
                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-green-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                    Copié!
                  </span>
                )}
              </button>
              
              {/* Bouton Quitter */}
              <button
                onClick={leaveMeeting}
                className="group relative flex items-center justify-center w-12 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white transition-all duration-200"
                title="Quitter la réunion"
              >
                <Phone className="h-5 w-5 rotate-[135deg]" />
              </button>
            </div>
          </div>
        </div>

        {/* Message d'erreur si présent */}
        {error && (
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
            <div className="bg-yellow-900/90 border border-yellow-600 text-yellow-100 px-4 py-3 rounded-lg shadow-lg">
              <div className="flex items-start gap-2">
                <svg className="h-5 w-5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold mb-1">Information</p>
                  <p className="text-xs">{error}</p>
                </div>
                <button
                  onClick={() => setError('')}
                  className="text-yellow-300 hover:text-yellow-100 ml-2"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Zone principale avec layout 80/20 */}
        <div className="w-full h-screen pt-12 pb-20 flex">
          {/* Section principale (80%) - Écran partagé ou vidéos */}
          <div className="w-[80%] h-full flex flex-col p-4">
            {/* Afficher le stream sélectionné manuellement ou l'écran partagé par défaut */}
            {(() => {
              // PRIORITÉ 1: Stream sélectionné manuellement par l'utilisateur
              if (selectedStreamForMainView) {
                const { type, uid } = selectedStreamForMainView;
                
                if (type === 'screen' && remoteScreenShares[uid]) {
                  const screenTrack = remoteScreenShares[uid];
                  const userName = remoteUserNames[uid] || `Utilisateur ${uid}`;
                  
                  return (
                    <div key={`selected-screen-${uid}`} className="relative bg-black rounded-lg overflow-hidden w-full h-full border-4 border-purple-500">
                      <div id={`remote-screen-${uid}`} className="w-full h-full" />
                      <div className="absolute top-2 left-2 bg-purple-600/90 text-white px-3 py-1 rounded text-sm font-semibold z-10">
                        📺 {userName} partage son écran
                      </div>
                      <button
                        onClick={() => setSelectedStreamForMainView(null)}
                        className="absolute top-2 right-2 bg-black/70 hover:bg-black/90 text-white p-2 rounded z-10"
                        title="Désélectionner"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                }
                
                if (type === 'video') {
                  const user = remoteUsers.find(u => u.uid === uid);
                  if (user && user.videoTrack) {
                    const userName = remoteUserNames[uid] || `Utilisateur ${uid}`;
                    
                    return (
                      <div key={`selected-video-${uid}`} className="relative bg-black rounded-lg overflow-hidden w-full h-full border-4 border-blue-500">
                        <div id={`remote-video-${uid}`} className="w-full h-full" />
                        <div className="absolute top-2 left-2 bg-blue-600/90 text-white px-3 py-1 rounded text-sm font-semibold z-10">
                          📹 {userName}
                        </div>
                        <button
                          onClick={() => setSelectedStreamForMainView(null)}
                          className="absolute top-2 right-2 bg-black/70 hover:bg-black/90 text-white p-2 rounded z-10"
                          title="Désélectionner"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  }
                }
              }
              
              // PRIORITÉ 2: Écran partagé local (si présent)
              const hasLocalScreenShare = isScreenSharing && screenTrack;
              if (hasLocalScreenShare) {
                return (
                  <div className="relative bg-black rounded-lg overflow-hidden w-full h-full border-4 border-green-500">
                    <div ref={screenShareRef} className="w-full h-full" />
                    <div className="absolute top-2 left-2 bg-green-600/90 text-white px-3 py-1 rounded text-sm font-semibold z-10">
                      📺 Votre écran partagé
                    </div>
                  </div>
                );
              }
              
              // PRIORITÉ 3: Premier écran partagé distant (si présent)
              const hasRemoteScreenShare = Object.keys(remoteScreenShares).length > 0;
              if (hasRemoteScreenShare) {
                const firstScreenShareUid = Object.keys(remoteScreenShares)[0];
                const screenTrack = remoteScreenShares[firstScreenShareUid];
                const userName = remoteUserNames[firstScreenShareUid] || `Utilisateur ${firstScreenShareUid}`;
                const isSelected = selectedStreamForMainView && 
                  selectedStreamForMainView.type === 'screen' && 
                  selectedStreamForMainView.uid === firstScreenShareUid;
                
                return (
                  <div key={`screen-${firstScreenShareUid}`} className="relative bg-black rounded-lg overflow-hidden w-full h-full border-4 border-purple-500 group">
                    <div id={`remote-screen-${firstScreenShareUid}`} className="w-full h-full" />
                    <div className="absolute top-2 left-2 bg-purple-600/90 text-white px-3 py-1 rounded text-sm font-semibold z-10">
                      📺 {userName} partage son écran
                    </div>
                    {/* Icône de sélection sur l'écran partagé */}
                    <button
                      onClick={() => {
                        if (isSelected) {
                          setSelectedStreamForMainView(null);
                        } else {
                          setSelectedStreamForMainView({
                            type: 'screen',
                            uid: parseInt(firstScreenShareUid)
                          });
                        }
                      }}
                      className={`absolute top-2 right-2 p-2 rounded transition-all z-20 ${
                        isSelected 
                          ? 'bg-blue-600 text-white shadow-lg' 
                          : 'bg-black/50 hover:bg-black/70 text-gray-300 hover:text-white opacity-0 group-hover:opacity-100'
                      }`}
                      title={isSelected ? 'Désélectionner' : 'Sélectionner pour affichage principal'}
                    >
                      {isSelected ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        <Maximize2 className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                );
              }
              
              // Si aucun stream sélectionné et aucun écran partagé, afficher les vidéos
              return null;
            })()}
            
            {/* Zone des vidéos (caméras) - seulement si pas de stream sélectionné et pas d'écran partagé */}
            {!selectedStreamForMainView && !isScreenSharing && Object.keys(remoteScreenShares).length === 0 && (
              <div className="flex flex-wrap gap-4 flex-1 overflow-auto">
                {/* Vidéo locale */}
                <div className="relative bg-black rounded-lg overflow-hidden" style={{ 
                  width: remoteUsers.length === 0 ? '100%' : 'calc(50% - 0.5rem)', 
                  height: remoteUsers.length === 0 ? '100%' : 'calc(50% - 0.5rem)',
                  minHeight: '200px'
                }}>
                  <div ref={localVideoRef} className="w-full h-full" style={{ display: localVideoTrack && isVideoEnabled ? 'block' : 'none' }} />
                  {!localVideoTrack && (
                    <div className="w-full h-full flex items-center justify-center bg-gray-900 absolute inset-0">
                      <div className="text-center">
                        <VideoOff className="h-16 w-16 text-gray-500 mx-auto mb-2" />
                        <p className="text-gray-400 text-sm">Vidéo non disponible</p>
                      </div>
                    </div>
                  )}
                  {!isVideoEnabled && localVideoTrack && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
                      <VideoOff className="h-16 w-16 text-gray-500" />
                    </div>
                  )}
                  {!localVideoTrack && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/90">
                      <VideoOff className="h-16 w-16 text-gray-500 mb-2" />
                      <p className="text-gray-400 text-sm mb-2">Vidéo non disponible</p>
                      <p className="text-gray-500 text-xs mb-4 text-center px-4">Vous pouvez continuer en mode audio seulement</p>
                      <div className="flex flex-col items-center gap-2">
                        <button
                          onClick={async () => {
                            try {
                              // Essayer d'obtenir les caméras disponibles (peut échouer si pas de permissions)
                              let cameras = [];
                              try {
                                cameras = await safeGetCameras();
                                setAvailableCameras(cameras);
                              } catch (camerasErr) {
                                console.warn('Impossible d\'obtenir la liste des caméras:', camerasErr);
                              }
                              
                              let videoTrack = null;
                              let cameraUsed = false;
                              
                              if (cameras.length > 1) {
                                for (const camera of cameras) {
                                  try {
                                    videoTrack = await safeCreateVideoTrack(camera.deviceId);
                                    setSelectedCameraId(camera.deviceId);
                                    cameraUsed = true;
                                    break;
                    } catch (err) {
                                    console.log(`Caméra ${camera.label} non disponible`);
                                    continue;
                                  }
                                }
                              }
                              
                              if (!cameraUsed) {
                                videoTrack = await safeCreateVideoTrack();
                              }
                              
                              setLocalVideoTrack(videoTrack);
                              if (client) {
                                const connectionState = client.connectionState;
                                if (connectionState === 'CONNECTED' || connectionState === 'CONNECTING') {
                                  await safePublishVideoTrack(videoTrack);
                                  if (localVideoRef.current) {
                                    localVideoRef.current.style.display = 'block';
                                    // Forcer un reflow pour Chrome
                                    await new Promise(resolve => setTimeout(resolve, 50));
                                    await safePlay(videoTrack, localVideoRef.current, 'local-video');
                                    
                                    // Vérification spécifique Chrome
                                    setTimeout(() => {
                                      if (localVideoRef.current) {
                                        const videoEl = localVideoRef.current.querySelector('video');
                                        if (!videoEl || videoEl.paused) {
                                          console.warn('⚠️ Chrome: Réessai affichage vidéo locale');
                                          safePlay(videoTrack, localVideoRef.current, 'local-video').catch(console.error);
                                        }
                                      }
                                    }, 300);
                                  }
                                }
                              }
                              setIsVideoEnabled(true);
                              setError('');
                            } catch (err) {
                              console.error('Erreur activation caméra:', err);
                              let errorMsg = '';
                              if (err.message && (err.message.includes('Permission denied') || err.message.includes('PERMISSION_DENIED'))) {
                                errorMsg = 'Permission refusée. Cliquez sur l\'icône de cadenas dans la barre d\'adresse et autorisez la caméra.';
                              } else if (err.message && (err.message.includes('NotReadableError') || err.message.includes('NOT_READABLE'))) {
                                // Essayer avec la caméra par défaut
                                try {
                                  const videoTrack = await safeCreateVideoTrack();
                                  setLocalVideoTrack(videoTrack);
                                  if (client) {
                                    const connectionState = client.connectionState;
                                    if (connectionState === 'CONNECTED' || connectionState === 'CONNECTING') {
                                      await safePublishVideoTrack(videoTrack);
                                      if (localVideoRef.current) {
                                        localVideoRef.current.style.display = 'block';
                                        // Forcer un reflow pour Chrome
                                        await new Promise(resolve => setTimeout(resolve, 50));
                                        await safePlay(videoTrack, localVideoRef.current, 'local-video');
                                        
                                        // Vérification spécifique Chrome
                                        setTimeout(() => {
                                          if (localVideoRef.current) {
                                            const videoEl = localVideoRef.current.querySelector('video');
                                            if (!videoEl || videoEl.paused) {
                                              console.warn('⚠️ Chrome: Réessai affichage vidéo locale');
                                              safePlay(videoTrack, localVideoRef.current, 'local-video').catch(console.error);
                                            }
                                          }
                                        }, 300);
                                      }
                                    }
                                  }
                                  setIsVideoEnabled(true);
                                  setError('');
                                  return;
                                } catch (retryErr) {
                                  errorMsg = 'La caméra est déjà utilisée par un autre navigateur ou application. Fermez les autres onglets/applications vidéo ou continuez en mode audio seulement.';
                                }
                              } else if (err.message && (err.message.includes('NotFoundError') || err.message.includes('DEVICE_NOT_FOUND'))) {
                                errorMsg = 'Aucune caméra détectée. Vérifiez que votre caméra est connectée.';
                              } else if (err.message && err.message.includes('CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS')) {
                                errorMsg = '⚠️ Impossible d\'activer la caméra: un partage d\'écran est actif. Arrêtez d\'abord le partage d\'écran.';
                              } else {
                                errorMsg = 'Impossible d\'activer la caméra: ' + (err.message || 'Erreur inconnue');
                              }
                              setError(errorMsg);
                              setTimeout(() => setError(''), 5000);
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                        >
                          Activer la caméra
                        </button>
                        {availableCameras.length > 1 && (
                          <p className="text-xs text-gray-400 text-center px-2">
                            {availableCameras.length} caméra(s) détectée(s) - Essai automatique
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
                    {displayName} (Vous)
                    {!localVideoTrack && <span className="ml-2 text-xs text-gray-400">- Audio seulement</span>}
                  </div>
                </div>

              {/* Vidéos distantes */}
              {remoteUsers.length > 0 && (
                <div className="flex flex-wrap gap-4" style={{ width: 'calc(50% - 0.5rem)', minHeight: '200px' }}>
                  {remoteUsers.map((user) => {
                    const userName = remoteUserNames[user.uid] || (user.uid ? `Utilisateur ${user.uid}` : 'Participant');
                    const hasScreenShare = remoteScreenShares[user.uid] !== undefined;
                    const hasVideo = user.hasVideo && user.videoTrack;
                    const isSelected = selectedStreamForMainView && 
                      ((selectedStreamForMainView.type === 'screen' && selectedStreamForMainView.uid === user.uid && hasScreenShare) ||
                       (selectedStreamForMainView.type === 'video' && selectedStreamForMainView.uid === user.uid && hasVideo));
                    
                    return (
                      <div
                        key={user.uid}
                        className="relative bg-black rounded-lg overflow-hidden group"
                        style={{ width: '100%', height: 'calc(50% - 0.5rem)', minHeight: '200px' }}
                      >
                        {user.videoTrack ? (
                          <div id={`remote-video-${user.uid}`} className="w-full h-full" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-900">
                            <div className="text-center">
                              <div className="w-20 h-20 rounded-full bg-blue-600 flex items-center justify-center mx-auto mb-2">
                                <span className="text-2xl font-bold text-white">
                                  {userName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <VideoOff className="h-8 w-8 text-gray-500 mx-auto" />
                            </div>
                          </div>
                        )}
                        <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-sm z-10">
                          {userName}
                          {!user.hasVideo && <span className="ml-2 text-xs text-gray-400">- Audio seulement</span>}
                        </div>
                        {/* Icône de sélection directement sur la vidéo */}
                        {(hasScreenShare || hasVideo) && (
                          <button
                            onClick={() => {
                              if (isSelected) {
                                setSelectedStreamForMainView(null);
                              } else {
                                setSelectedStreamForMainView({
                                  type: hasScreenShare ? 'screen' : 'video',
                                  uid: user.uid
                                });
                              }
                            }}
                            className={`absolute top-2 right-2 p-2 rounded transition-all z-20 ${
                              isSelected 
                                ? 'bg-blue-600 text-white shadow-lg' 
                                : 'bg-black/50 hover:bg-black/70 text-gray-300 hover:text-white opacity-0 group-hover:opacity-100'
                            }`}
                            title={isSelected ? 'Désélectionner' : 'Afficher en grand (80%)'}
                          >
                            {isSelected ? (
                              <Check className="h-5 w-5" />
                            ) : (
                              <Maximize2 className="h-5 w-5" />
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              </div>
            )}
          </div>

          {/* Sidebar droite (20%) - Liste des participants */}
          <div className="w-[20%] h-full bg-gray-800/50 border-l border-gray-700 overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-300">
                  Participants ({remoteUsers.length + 1})
                </h3>
              </div>
              
              <div className="space-y-2">
                {/* Carte utilisateur local */}
                <div className="bg-gray-700/50 rounded-lg p-3 flex items-center gap-3 hover:bg-gray-700/70 transition-colors">
                  <div className="flex-shrink-0">
                    {localVideoTrack && isVideoEnabled ? (
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center overflow-hidden relative">
                        <span className="text-sm font-bold text-white absolute z-10">
                          {displayName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-sm font-bold text-white">
                          {displayName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {displayName} (Vous)
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {isAudioEnabled ? (
                        <Mic className="h-3 w-3 text-green-400" />
                      ) : (
                        <MicOff className="h-3 w-3 text-red-400" />
                      )}
                      {localVideoTrack && isVideoEnabled ? (
                        <Video className="h-3 w-3 text-green-400" />
                      ) : (
                        <VideoOff className="h-3 w-3 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Cartes utilisateurs distants */}
                {remoteUsers.map((user) => {
                  const userName = remoteUserNames[user.uid] || (user.uid ? `Utilisateur ${user.uid}` : 'Participant');
                  const firstLetter = userName.charAt(0).toUpperCase();
                  const colors = ['bg-red-600', 'bg-green-600', 'bg-blue-600', 'bg-purple-600', 'bg-yellow-600', 'bg-pink-600'];
                  const colorIndex = parseInt(user.uid) % colors.length;
                  
                  // Vérifier si cet utilisateur a un écran partagé
                  const hasScreenShare = remoteScreenShares[user.uid] !== undefined;
                  const hasVideo = user.hasVideo && user.videoTrack;
                  
                  // Vérifier si ce stream est sélectionné pour l'affichage principal
                  const isSelected = selectedStreamForMainView && 
                    ((selectedStreamForMainView.type === 'screen' && selectedStreamForMainView.uid === user.uid && hasScreenShare) ||
                     (selectedStreamForMainView.type === 'video' && selectedStreamForMainView.uid === user.uid && hasVideo));
                  
                  return (
                    <div key={user.uid} className={`bg-gray-700/50 rounded-lg p-3 flex items-center gap-3 hover:bg-gray-700/70 transition-colors ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
                      <div className="flex-shrink-0">
                        {user.videoTrack && !hasScreenShare ? (
                          <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden relative">
                            <div id={`remote-video-${user.uid}`} className="w-full h-full" />
                            <div className="absolute inset-0 bg-black/20" />
                          </div>
                        ) : (
                          <div className={`w-10 h-10 rounded-full ${colors[colorIndex]} flex items-center justify-center`}>
                            <span className="text-sm font-bold text-white">
                              {firstLetter}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {userName}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {user.hasAudio ? (
                            <Mic className="h-3 w-3 text-green-400" />
                          ) : (
                            <MicOff className="h-3 w-3 text-red-400" />
                          )}
                          {hasScreenShare ? (
                            <Monitor className="h-3 w-3 text-purple-400" title="Partage son écran" />
                          ) : hasVideo ? (
                            <Video className="h-3 w-3 text-green-400" />
                          ) : (
                            <VideoOff className="h-3 w-3 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Afficher aussi les écrans partagés qui ne sont pas dans remoteUsers (si l'utilisateur n'a pas de caméra) */}
                {Object.keys(remoteScreenShares).map((uid) => {
                  const userExists = remoteUsers.find(u => u.uid.toString() === uid.toString());
                  if (userExists) return null; // Déjà affiché ci-dessus
                  
                  const userName = remoteUserNames[uid] || `Utilisateur ${uid}`;
                  const firstLetter = userName.charAt(0).toUpperCase();
                  const colors = ['bg-red-600', 'bg-green-600', 'bg-blue-600', 'bg-purple-600', 'bg-yellow-600', 'bg-pink-600'];
                  const colorIndex = parseInt(uid) % colors.length;
                  
                  const isSelected = selectedStreamForMainView && 
                    selectedStreamForMainView.type === 'screen' && 
                    selectedStreamForMainView.uid.toString() === uid.toString();
                  
                  return (
                    <div key={`screen-only-${uid}`} className={`bg-gray-700/50 rounded-lg p-3 flex items-center gap-3 hover:bg-gray-700/70 transition-colors ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full ${colors[colorIndex]} flex items-center justify-center`}>
                          <span className="text-sm font-bold text-white">
                            {firstLetter}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {userName}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Monitor className="h-3 w-3 text-purple-400" title="Partage son écran" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Panneau de Chat */}
        <ChatPanel
          isOpen={chat.chatOpen}
          onClose={() => chat.setChatOpen(false)}
          onOpen={() => chat.setChatOpen(true)}
          messages={chat.messages}
          newMessage={chat.newMessage}
          setNewMessage={chat.setNewMessage}
          sendMessage={chat.sendMessage}
          handleTyping={chat.handleTyping}
          formatTime={chat.formatTime}
          isTyping={chat.isTyping}
          client={client}
          messagesEndRef={chat.messagesEndRef}
          chatInputRef={chat.chatInputRef}
          socketConnected={chat.socketConnected}
          isMinimized={chatMinimized}
          onMinimize={() => setChatMinimized(!chatMinimized)}
        />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <div className="text-center pt-16 pb-10">
        <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Visioconférence
        </h1>
        <p className="mt-4 text-xl text-gray-300">Réunion instantanée & sécurisée - Durée illimitée</p>
      </div>

      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
        <div className="max-w-2xl w-full bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
          <div className="space-y-6">
            {/* Nom d'affichage */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Votre nom
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Entrez votre nom"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && displayName.trim()) {
                    startMeeting();
                  }
                }}
              />
            </div>

            {/* Nom de room (optionnel) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nom de la réunion {channelName ? '(réunion existante)' : '(optionnel)'}
              </label>
              <input
                type="text"
                value={channelName}
                onChange={(e) => setChannelName(e.target.value)}
                placeholder={channelName ? channelName : "Laissez vide pour générer automatiquement"}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && displayName.trim()) {
                    if (channelName) {
                      joinExistingMeeting();
                    } else {
                    startMeeting();
                    }
                  }
                }}
              />
              <p className="mt-2 text-xs text-gray-400">
                {channelName 
                  ? "Vous allez rejoindre une réunion existante" 
                  : "Si vide, un nom unique sera généré automatiquement"}
              </p>
            </div>

            {/* Boutons de démarrage/rejoindre */}
            <div className="flex gap-3">
              {channelName ? (
                <>
                  <button
                    onClick={joinExistingMeeting}
                    disabled={!displayName.trim() || !channelName.trim() || loading}
                    className="flex-1 px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl shadow-2xl transition transform hover:scale-105 font-semibold text-lg flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Connexion...
                      </>
                    ) : (
                      <>
                        <Users className="h-5 w-5" />
                        Rejoindre la réunion
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setChannelName('')}
                    className="px-4 py-4 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors"
                    title="Créer une nouvelle réunion"
                  >
                    <Video className="h-5 w-5" />
                  </button>
                </>
              ) : (
            <button
              onClick={startMeeting}
                  disabled={!displayName.trim() || loading}
              className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl shadow-2xl transition transform hover:scale-105 font-semibold text-lg flex items-center justify-center gap-2"
            >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Création de la réunion...
                    </>
                  ) : (
                    <>
              <Video className="h-5 w-5" />
              Démarrer la réunion
                    </>
                  )}
            </button>
              )}
            </div>

            {error && (
              <div className="p-4 bg-red-900/70 border border-red-600 rounded-xl">
                <p className="text-red-100">{error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-12 max-w-3xl w-full bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Comment ça fonctionne ?
          </h3>
          <div className="space-y-3 text-gray-300">
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold">1</span>
              <p><strong>Entrez votre nom</strong> et optionnellement un nom de réunion personnalisé</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-sm font-bold">2</span>
              <p><strong>Cliquez sur "Démarrer la réunion"</strong> pour créer une nouvelle réunion</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-sm font-bold">3</span>
              <p><strong>Partagez le lien</strong> de la réunion avec vos participants en cliquant sur le bouton "Partager"</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center text-sm font-bold">4</span>
              <p><strong>Fonctionnalités incluses:</strong> Chat, partage d'écran, contrôles audio/vidéo, et plus encore!</p>
            </div>
          </div>
          
          {/* Note importante sur Agora */}
          <div className="mt-6 p-4 bg-blue-900/30 border border-blue-500/50 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-300 mb-1">Réunions illimitées avec Agora.io</p>
                <p className="text-xs text-blue-200">
                  Cette application utilise <strong>Agora.io</strong> pour les visioconférences, ce qui permet des réunions de <strong>durée illimitée</strong> (10 000 minutes gratuites par mois). 
                  Les réunions sont sécurisées et incluent toutes les fonctionnalités avancées : chat, partage d'écran, contrôles audio/vidéo, et plus encore.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Avantages */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl w-full">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
            <div className="text-3xl mb-2">⏱️</div>
            <h4 className="font-semibold mb-1">Durée illimitée</h4>
            <p className="text-sm text-gray-400">10 000 min/mois gratuites</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
            <div className="text-3xl mb-2">🔒</div>
            <h4 className="font-semibold mb-1">Sécurisé</h4>
            <p className="text-sm text-gray-400">Chiffrement de bout en bout</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
            <div className="text-3xl mb-2">🚀</div>
            <h4 className="font-semibold mb-1">Illimité</h4>
            <p className="text-sm text-gray-400">Pas de limite de participants</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoCallPage;
